import {
  OutboundCarrierGateError,
  readOutboundCarrierBlock,
  withOutboundCarrierGates,
  withArchivedOutboundLabels,
} from '@/utils/fulfillmentOutboundGate';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { getConfig } from '../config';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { getAuthorizationToken, getIdentityAssertion } from '@/apps/apis/claudeCodeDashboard/token';

/** One product line as the list shows it — the KR name column plus the SKU column's sourcing trio. */
export interface FulfillmentOrderLineSummary {
  sku: string;
  productNameKr: string | null;
  /** The English name, quantity and unit price a carrier export declares per item. */
  productName: string | null;
  quantity: number | null;
  unitPrice: number | null;
  sourcingRoute: string | null;
  /** The line's purchase order number. */
  sourcingOrderNumber: string | null;
  /** Every inbound waybill registered on the line, comma-joined. */
  sourcingTrackingNumber: string | null;
  /** Where the line was bought; null when unrecorded, absent on a server that predates it. */
  purchasePlatform?: SourcingPlatform | null;
  /** Null on a line that is not a live JIT purchase; absent on a server that predates it. */
  purchaseState?: FulfillmentLinePurchaseState | null;
}

/** How far a live JIT line's purchase has got: V1 read the middle one as "Preparing" and the first as "NA". */
export type FulfillmentLinePurchaseState = 'NOT_PURCHASED' | 'PURCHASED_NO_TRACKING' | 'TRACKING_REGISTERED';

/** One side of a list filter axis that asks whether a live JIT line carries a fact. */
export type FulfillmentPurchasePresence = 'PRESENT' | 'ABSENT';

/** Names the orders the bundle came up short on, so the sheet count is never a silent surprise. */
export const SKIPPED_ORDERS_HEADER = 'x-label-bundle-skipped';

/** One batch's archived labels as a single document, and the orders that put nothing in it. */
export interface LabelBundle {
  pdf: Blob;
  skipped: number[];
}

/** The outbound parcel behind the Provider / Provider Order ID / Tracking No columns. */
export interface FulfillmentOrderShipmentSummary {
  provider: string | null;
  providerOrderId: string | null;
  trackingNumber: string | null;
}

export interface FulfillmentOrderRow {
  orderId: number;
  channel: string;
  countryCode: string | null;
  orderPlacedAt: string | null;
  fulfillmentStatus: string;
  wcStatus: string | null;
  /** Server authoritative; absent or null before rollout/backfill, never inferred from other statuses. */
  csStatus?: string | null;
  /** How CS resolved the issue; set only while csStatus is resolved, absent on a server that predates it. */
  csResolutionMethod?: CsResolutionMethod | null;
  totalQuantity: number;
  isMarketing: boolean;
  campaignId: string | null;
  picUser: string | null;
  /** Null means the order is not held. Orthogonal to fulfillmentStatus — a READY order can be held. */
  holdReason?: string | null;
  heldBy?: string | null;
  heldAt?: string | null;
  /** Decoded JSON columns; shapes vary by ingestion source, so the table reads them defensively. */
  customer?: unknown;
  shipping?: unknown;
  amounts?: unknown;
  lines?: FulfillmentOrderLineSummary[];
  shipment?: FulfillmentOrderShipmentSummary | null;
  /** How a RESOLVED order was resolved; null on any other status, absent on a server that predates it. */
  resolutionKind?: FulfillmentResolutionKind | null;
  /** How many notes the order's thread holds (SSD-7567); absent on a server that predates it. */
  commentCount?: number;
  /** When a change-of-mind cancel stopped being allowed (SSD-7450); null while it still is, absent on an older server. */
  cancelLockedAt?: string | null;
  cancelLockSource?: CancelLockSource | null;
}

/**
 * What locked the order against a change-of-mind cancel (SSD-7450): the first purchase on any of its lines, its
 * outbound, or the one-off backfill of orders that were already past either before the lock existed.
 */
export type CancelLockSource = 'FIRST_PURCHASE' | 'OUTBOUND' | 'BACKFILL';

/** Mirrors the backend canon (core FulfillmentTypes): raw snake_case keys, every field optional. */
export interface FulfillmentOrderSegment {
  type?: string;
  campaign_id?: string;
  influencer_id?: string;
  brand_slug?: string;
  brand_name?: string;
  order_id?: string;
}

/**
 * The COGS report message as Send would post it (SSD-7328): Slack mrkdwn in `text`, the names behind its `<@id>`
 * mentions, and the line's own per-unit sale price with its won figure at the store rate (null where there is none).
 */
export interface CogsReportPreview {
  text: string;
  mentionNames: Record<string, string>;
  currency: string;
  salePrice: number | null;
  salePriceKrw: number | null;
}

/** One press of the COGS report's Send. The sale price is not sent: the server reads what the line sold for. */
export interface ReportLineCogsRequest {
  /** Per unit, in whole KRW. */
  purchasePrice: number;
  /** One id per open of the form; a retried request with it is answered without a second message. */
  requestId: string;
}

/** The report reached the catalog channel; `salePriceKrw` is the won figure the message carried. */
export interface ReportLineCogsResponse {
  posted: true;
  salePriceKrw: number | null;
}

/** A field a catalog request type takes, as the server's schema describes it (SSD-7384). */
export interface CatalogRequestFieldSpec {
  key: string;
  label: string;
  kind: 'text' | 'choice' | 'choices' | 'url' | 'sku';
  required: boolean;
  options?: ReadonlyArray<{ value: string; label: string }>;
  hosts?: readonly string[];
  maxLength?: number;
  placeholder?: string;
}

/** One thing an operator can ask the catalog team about a line, and the fields that ask needs. */
export interface CatalogRequestTypeSpec {
  type: string;
  group: string;
  label: string;
  fields: readonly CatalogRequestFieldSpec[];
}

/** The request types and their fields, so the form is built from the table the server checks against. */
export interface CatalogRequestSchema {
  version: number;
  groups: ReadonlyArray<{ group: string; label: string }>;
  types: readonly CatalogRequestTypeSpec[];
}

/** A field's value as the form sends it: text, one choice, or several choices. */
export type CatalogRequestFields = Record<string, string | string[]>;

/** The catalog request message as Send would post it: Slack mrkdwn in `text` and the names behind its mentions. */
export interface CatalogRequestPreview {
  text: string;
  mentionNames: Record<string, string>;
  type: string;
}

/** The body of a preview: the schema the form was built from, the request type and the fields filled so far. */
export interface PreviewCatalogRequestRequest {
  schemaVersion: number;
  type: string;
  fields: CatalogRequestFields;
}

/** One press of the catalog request's Send: the same, plus one id per open of the form. */
export interface SendCatalogRequestRequest extends PreviewCatalogRequestRequest {
  requestId: string;
}

/** The request reached the catalog channel. */
export interface SendCatalogRequestResponse {
  posted: true;
  type: string;
}

export interface ListOrdersParams {
  limit?: number;
  band?: 'wc' | 'marketing' | 'internal';
  channel?: string;
  fulfillmentStatus?: string;
  /** Server-stored CS status, independent of warehouse progress, WooCommerce and manual hold. */
  csStatus?: string;
  /** Narrows resolved orders by how CS resolved them; meaningful only alongside csStatus resolved. */
  csResolutionMethod?: CsResolutionMethod;
  /** Inclusive YYYY-MM-DD bounds read as warehouse-local days, matching what the list renders. */
  startDate?: string;
  endDate?: string;
  countryCode?: string;
  /** Case-insensitive substring of the batch PIC. */
  pic?: string;
  /** One term matched against the buyer and the lines; the two flags widen it. */
  search?: string;
  searchOrderNo?: boolean;
  searchTracking?: boolean;
  /** Widen the term to the order's comment thread, the way the Comments tick reads. */
  searchComments?: boolean;
  searchPic?: boolean;
  /** Rows to skip before the page starts; the list pages by number rather than by cursor. */
  offset?: number;
  beforeOrderId?: number;
  /** Manual hold only (holdReason is present), independent of CS status. */
  onHoldOnly?: boolean;
  /** Orders with a live JIT line whose purchase order number is (or is not) recorded. */
  purchaseOrder?: FulfillmentPurchasePresence;
  /** Orders with a live JIT line that has (or has no) inbound waybill; with purchaseOrder, the same line must match both. */
  inboundTracking?: FulfillmentPurchasePresence;
}

/** One row of the cross-order comments hub [G-7]; the order number travels with it for the click-through. */
export interface FulfillmentCommentRow {
  /** Null on a note written against an inbound request, which the hub links by its arrival number. */
  orderId: number | null;
  docId?: string | null;
  inboundNo?: string | null;
  text: string | null;
  author: string | null;
  commentedAt: string | null;
  /** Whether the operator who asked has starred this one — stars are per-operator, not per-comment. */
  starred: boolean;
  /** False where the mark table cannot key the row; the star is then not offered rather than failing. */
  starrable?: boolean;
}

export interface SearchCommentsParams {
  limit?: number;
  search?: string;
  /** Handle whose @mentions the Mentions pane lists. */
  mentions?: string;
  /** Who is reading: the API key authenticates the dashboard, not the person at it. */
  operator?: string;
  savedOnly?: boolean;
}

export interface SetCommentStarRequest {
  operator: string;
  orderId: number;
  commentedAt: string;
  starred: boolean;
}

export interface SearchCommentsResponse {
  comments: FulfillmentCommentRow[];
  resultCount: number;
  /** Unread mentions among the rows this page returned, so it never exceeds the page limit. */
  unreadMentionCount: number;
  /**
   * Every mention of the operator written since they last marked everything read, independent of the
   * page limit, the search term and the pane — the badge number. Absent on a backend that predates it.
   */
  unreadMentionTotal?: number;
}

export interface ListOrdersResponse {
  orders: FulfillmentOrderRow[];
  nextCursor: number | null;
  /** Orders matching the filter set, which is not the number on this page. Absent on a backend that predates the count. */
  totalCount?: number;
}

export interface FulfillmentOrderLine {
  lineNo: number;
  sku: string;
  qtyOrdered: number;
  totalQuantity: number;
  lineStatus: string;
  isSample: boolean;
  sourcingRoute?: string;
  /** Server-decided: whether this arrival can be placed back; absent on legacy responses. */
  inboundReversible?: boolean;
  /** Every shelf the line's reservations hold, WH#00 included, with each one's quantity; absent on a server that predates it. */
  locations?: FulfillmentLocationQty[];
  latestInventoryCount?: number;
  /** Ingestion-snapshot presentation for the 18-column line table; absent on legacy responses. */
  brand?: string | null;
  productName?: string | null;
  productNameKr?: string | null;
  size?: string | null;
  productId?: number | null;
  originalSku?: string | null;
  originalQuantity?: number | null;
  originalProductName?: string | null;
  originalSize?: string | null;
  originalProductNameKr?: string | null;
  originalSubtotal?: number | null;
  originalTotal?: number | null;
  subtotal?: number | null;
  total?: number | null;
  productCost?: number | null;
  purchaseOrderNumber?: string | null;
  purchaseOrderDate?: string | null;
  /** Where the line was bought; null when unrecorded, absent on a server that predates it. */
  purchasePlatform?: SourcingPlatform | null;
  purchaseUrl?: string | null;
  lineTrackingNumber?: string | null;
  /** Common inbound courier, or the saved pre-waybill selection; null when no common company is recorded. */
  lineDeliveryCompany?: string | null;
  /** Exact inbound parcel pairings; absent on servers predating SSD-7069. */
  lineTrackingCouriers?: LineTrackingCourier[];
  imageUrl?: string | null;
  /** When the row was last written; a guarded edit carries it back so a line that moved since is refused [T05]. */
  updatedAt?: string | null;
  /** The raw ingestion snapshot; a line mirrored from OMS Center keeps the whole V1 line item here. */
  snapshot?: unknown;
  /** Seller class the line was bought from (SSD-6752); null when unrecorded, absent on a server that predates it. */
  sellerType?: SourcingSellerType | null;
  /** The purchase-time price comparison (SSD-6756); null when unrecorded, absent on a server that predates it. */
  sourcingComparison?: LineSourcingComparison | null;
}

/** An actual domestic inbound parcel; the id and number guard edits to this exact pairing. */
export interface LineTrackingCourier {
  trackingId: string;
  number: string;
  carrier: string | null;
}

/**
 * How many of a line's units sit at one location (SSD-7236): a line's stock may come from several shelves.
 * `locationCode` is null only for a hold pinned to no shelf.
 */
export interface FulfillmentLocationQty {
  locationCode: string | null;
  qty: number;
}

/** Where a sourcing option is offered; mirrors the Facade core `SourcingPlatform`. */
export type SourcingPlatform = 'COUPANG' | 'NAVER' | 'OLIVE_YOUNG' | 'BRAND_OFFICIAL_SITE' | 'OTHER_RETAILER';

/** Platform-namespaced seller class; mirrors the Facade core `SourcingSellerType`. UNKNOWN is a recorded answer. */
export type SourcingSellerType =
  | 'COUPANG_ROCKET'
  | 'COUPANG_ROCKET_GROWTH'
  | 'COUPANG_SELLER_DELIVERY'
  | 'NAVER_BRAND_OFFICIAL_STORE'
  | 'NAVER_GENERAL_SELLER'
  | 'OLIVE_YOUNG'
  | 'BRAND_OFFICIAL_SITE'
  | 'OTHER_RETAILER'
  | 'UNKNOWN';

/** What the listing said about stock; only IN_STOCK and LIMITED can be bought. */
export type SourcingStockStatus = 'IN_STOCK' | 'LIMITED' | 'OUT_OF_STOCK' | 'NOT_LISTED' | 'UNKNOWN';

export type SourcingComparisonOutcome = 'COMPARED' | 'NO_ALTERNATIVE_AVAILABLE' | 'NOT_COMPARED';

/** One option seen at purchase time, priced in whole KRW (VAT included) for `quantityBasis` pieces. */
export interface SourcingAlternative {
  alternativeId: string;
  platform: SourcingPlatform;
  sellerType?: SourcingSellerType;
  sellerName?: string;
  url?: string;
  quantityBasis: number;
  unitPriceKrw?: number;
  itemsTotalKrw?: number;
  deliveryFeeKrw?: number;
  /** itemsTotalKrw + deliveryFeeKrw. */
  landedTotalKrw?: number;
  stockStatus: SourcingStockStatus;
  /** YYYY-MM-DD (KST). */
  expectedArrivalDate?: string;
  /** Most pieces one order may buy; absent means no limit. */
  quantityLimit?: number;
  note?: string;
}

/** The stored comparison; the server stamps schemaVersion, capturedAt, capturedBy and source. */
export interface LineSourcingComparison {
  schemaVersion: number;
  capturedAt: string;
  capturedBy: string;
  source: string;
  outcome: SourcingComparisonOutcome;
  /** YYYY-MM-DD (KST) arrive-in-time deadline. */
  requiredArrivalDate?: string;
  chosenAlternativeId?: string;
  alternatives: SourcingAlternative[];
}

/** The comparison as the screen sends it; the whole document replaces the stored one. */
export interface SourcingComparisonRequest {
  outcome: SourcingComparisonOutcome;
  requiredArrivalDate?: string;
  chosenAlternativeId?: string;
  alternatives: SourcingAlternative[];
}

/** An omitted part is left as stored and null clears it. */
export interface UpdateLineSourcingDetailRequest {
  sellerType?: SourcingSellerType | null;
  sourcingComparison?: SourcingComparisonRequest | null;
  /** The line's `updatedAt` as read; a line that moved since is refused with a 409. */
  expectedUpdatedAt?: string;
}

/** The five agent-filled fields an operator may correct inline; an omitted key is left untouched, null clears it. */
export interface MergeOrdersRequest {
  targetOrderId: number;
  sourceOrderIds: number[];
}

export interface MergeOrdersResponse {
  targetOrderId: number;
  movedLines: number;
  cancelledLines: number;
  mergedSourceOrderIds: number[];
  targetFulfillmentStatus: string;
}

export interface UpdateLineAgentTrackingRequest {
  /** Optional: the line's `updatedAt` as read, to refuse a line that moved since [T05]. */
  expectedUpdatedAt?: string;
  /** The line's display size, 1-64 characters; null puts back the size it was taken in with (SSD-7514). */
  size?: string | null;
  purchaseOrderNumber?: string | null;
  purchaseOrderDate?: string | null;
  purchasePlatform?: SourcingPlatform | null;
  productCost?: number | null;
  // No purchaseUrl: the Admin does not write a line's CP link; it belongs to SS Editor (SSD-7588).
  lineTrackingNumber?: string | null;
  /** Omitted preserves the company; null clears it without inventing a courier. */
  lineDeliveryCompany?: string | null;
  /** Explicit existing INBOUND/LINE parcel target; never broadcasts across parcels. */
  lineTrackingId?: string;
  /** Required with lineTrackingId; the number as the editor read it. */
  expectedTrackingNumber?: string;
}

export interface FulfillmentReservation {
  lineNo: number;
  sku: string;
  qty: number;
  kind: string;
  state: string;
  releasedReason: string | null;
  /** The shelf the reservation holds — where a Cancel Inbound puts the stock back. */
  locationCode: string | null;
}

export interface FulfillmentPendingParcel {
  bookingId: string;
  localOrderId: string;
  carrier: string;
  carrierOrderId: string | null;
  referenceNo: string | null;
  bookingState: string;
  needsAttention: boolean;
}

export interface FulfillmentTracking {
  number: string;
  /** Deleonex final Deleo reference (DL/ML) for the drawn sticker; number remains the courier waybill. */
  referenceNo?: string | null;
  /** Optional tracking-row creation timestamp; older responses may omit it. */
  createdAt?: string | null;
  carrier: string;
  kind: string;
  refId: string;
  unifiedStatus: string;
  /** Null until a carrier returns one; every tracking row on dev is null today. */
  labelUrl?: string | null;
  /** The carrier's own id for the shipment (YunExpress waybill, Deleo order id, Parxl PLS reference); absent on an older server. */
  carrierOrderId?: string | null;
  /** The unified tracking events V2 tracking intake recorded on the row (SSD-7091); absent on an older server. */
  events?: FulfillmentTrackingEventEntry[];
  /** What the carrier feeds last reported about the parcel (SSD-7091); null before the first report, absent on an older server. */
  snapshot?: FulfillmentTrackingSnapshot | null;
}

/** One unified tracking event on a V2 tracking row, in the shape V2 tracking intake stores it. */
export interface FulfillmentTrackingEventEntry {
  event: string;
  provider: string;
  providerEventCode: string;
  timestamp: string;
  description?: string;
  displayText: string;
  customerVisible: boolean;
}

/** One unified status the parcel passed through, with the carrier code that moved it there. */
export interface FulfillmentTrackingStatusHistoryEntry {
  status: string;
  provider: string;
  providerStatusCode: string;
  timestamp: string;
}

/** Which carrier feed a part of the snapshot came from; Parxl joins when the refresh stores it (SSD-7104). */
export type FulfillmentCarrierFeed = 'TRACKING_MORE' | 'YUN_EXPRESS' | 'DELEO' | 'PARXL';

/** One carrier feed's latest report about a parcel; `data` is that carrier's own payload, kept as it arrived. */
export interface FulfillmentCarrierSnapshotEntry {
  providerStatus: string | null;
  receivedAt: string;
  data: Record<string, unknown>;
}

/** The carrier-side state of one OUTBOUND tracking row (fulfillment_tracking_snapshot). */
export interface FulfillmentTrackingSnapshot {
  trackingId?: string;
  trackingNumber: string;
  trackingMoreId: string | null;
  deliveredAt: string | null;
  statusHistory: FulfillmentTrackingStatusHistoryEntry[];
  carrierSnapshot: Partial<Record<FulfillmentCarrierFeed, FulfillmentCarrierSnapshotEntry>>;
  lastReceivedAt: string;
}

/** One carrier checkpoint of a live trace. */
export interface FulfillmentTraceEvent {
  status: string | null;
  detailStatus?: string | null;
  eventTime: string | null;
  description: string | null;
  location?: string | null;
}

/** One parcel leg of a live trace (a carrier hand-over starts a new one). */
export interface FulfillmentTraceInfo {
  trackingNumber: string;
  mainTrackingNumber: string | null;
  carrierCode: string | null;
  carrierName: string | null;
  origin: string | null;
  destination: string | null;
  status: string | null;
  description: string | null;
  events: FulfillmentTraceEvent[];
}

/** One of an order's Zero Packing packing videos (SSD-7372). */
export interface PackingVideo {
  videoId: string;
  /** What the packer scanned: the order number, or its YunExpress order number. */
  scannedNumber: string;
  recordedAt: string;
  durationSeconds: number | null;
  stationName: string | null;
  /** When Zero Packing deletes the file (90 days after recording). */
  expiresAt: string | null;
  expired: boolean;
}

/** An order's packing videos, newest first; `connected` is false where the server has no Zero Packing key. */
export interface PackingVideoList {
  connected: boolean;
  videos: PackingVideo[];
}

/** A signed file link for the player. It dies in about ten minutes, so it is fetched per press and never kept. */
export interface PackingVideoPlayback {
  videoId: string;
  url: string;
  expiresAt: string;
}

/** A Zero Packing share page for the customer; it opens without signing in until `expiresAt`. */
export interface PackingVideoShareLink {
  videoId: string;
  url: string;
  expiresAt: string;
}

/** The carrier's live answer for a V2 tracking row, as the Facade's shipping providers normalize it. */
export interface FulfillmentTrackingTrace {
  provider: string;
  providerOrderId: string;
  status: string | null;
  statusCode: string | null;
  statusDescription: string | null;
  trackingInfoList: FulfillmentTraceInfo[];
}

export interface FulfillmentEvent {
  id: string;
  kind: string;
  operation: string;
  source: string;
  /** Null for a write no person made, such as a gig worker's purchase (source GIG). */
  actor: string | null;
  payload: unknown;
  occurredAt: string;
}

export interface FulfillmentOrderHeader {
  orderId: number;
  channel: string;
  countryCode: string | null;
  orderPlacedAt: string | null;
  fulfillmentStatus: string;
  wcStatus: string | null;
  /** Server authoritative; absent or null before rollout/backfill. */
  csStatus?: string | null;
  /** Set only while csStatus is resolved; absent on a server that predates it. */
  csResolutionMethod?: CsResolutionMethod | null;
  totalQuantity: number;
  isMarketing: boolean;
  isMysteryBox: boolean;
  campaignId: string | null;
  segments: FulfillmentOrderSegment[] | null;
  customer: unknown;
  shipping: unknown;
  amounts: unknown;
  holdReason: string | null;
  heldBy?: string | null;
  heldAt?: string | null;
  /** FS-051 shelf the packer parked the boxes on, editable inline from View Orders. */
  temporaryLocation?: string | null;
  picUser: string | null;
  createdAt: string;
  updatedAt: string;
  /** When a change-of-mind cancel stopped being allowed (SSD-7450); null while it still is, absent on an older server. */
  cancelLockedAt?: string | null;
  cancelLockSource?: CancelLockSource | null;
}

/** Manual CS changes record display/audit state; a confirmed, eligible cancellation may also cancel warehouse work. The server derives the actor from authentication. */
export type ManualCsStatus = 'delivered' | 'canceled' | 'resolved';

/** What CS did to resolve an issue: a record of the outcome, not a command to reship, redeliver or refund. */
export type CsResolutionMethod = 'reshipment' | 'redelivery' | 'refunded';

export interface PreOutboundCancelEligibility {
  orderId: number;
  eligible: boolean;
  reason: string | null;
}

export interface ChangeCsStatusResponse {
  orderId: number;
  csStatus: ManualCsStatus;
  /** Null once the order leaves resolved; absent on a server that predates the method. */
  csResolutionMethod?: CsResolutionMethod | null;
  warehouseCancellation?: { changed: boolean; cancelledLines: number };
  csStatusSource: 'AUTO' | 'MANUAL' | 'BACKFILL' | null;
  csStatusUpdatedAt: string | null;
  csStatusUpdatedBy: string | null;
  changed: boolean;
}

/** Hold payload; the backend derives the operator identity from authentication context. */
export interface HoldOrderRequest {
  reason: string;
}

export interface HoldOrderResponse {
  holdReason: string;
  heldBy: string;
  heldAt: string;
}

export interface ReleaseHoldResponse {
  cleared: boolean;
}

/** Who is booking, and the carrier to pin when routing by destination country is not what the operator wants. */
export interface CreateCarrierOrderRequest {
  operator: string;
  provider?: string;
}

/** What the carrier answered, as the tracking badge shows it back. */
export interface CarrierOrderResponse {
  carrier: string;
  carrierOrderId: string;
  trackingNumber: string;
  labelUrl: string | null;
}

/**
 * COPY leaves the source order as it is and the clone reserves its own stock (a reship or replacement).
 * SPLIT moves the cloned quantities off the source, with their reservations and their share of the amount,
 * so the two orders together still hold exactly what the customer bought (a split shipment).
 */
export type CloneOrderMode = 'COPY' | 'SPLIT';

/** The carriers a clone may book. FedEx is refused, as it was on the OMS clone. */
export type CloneOrderProvider = 'DELEO' | 'YUN_EXPRESS' | 'PARXL';

/** One source line to clone, counted in the line's physical units (its `totalQuantity`). */
export interface CloneOrderLineRequest {
  lineNo: number;
  qty: number;
}

export interface CloneOrderRequest {
  mode: CloneOrderMode;
  lines: CloneOrderLineRequest[];
  provider: CloneOrderProvider;
  reason: string;
  /** Repeating the same ask with the same key answers with the clone the first one made. */
  idempotencyKey: string;
}

/** One line of the new clone, as it was allocated. */
export interface CloneOrderLine {
  lineNo: number;
  sku: string;
  totalQuantity: number;
  lineStatus: string;
  sourcingRoute: string;
}

/** A clone always lands; only its carrier booking may fail, and then the clone books from its own page. */
export interface CloneOrderResponse {
  cloneOrderId: number;
  sourceOrderId: number;
  /** The first order of the clone chain; a clone of a clone still points at the order the customer placed. */
  rootOrderId?: number;
  mode?: CloneOrderMode;
  /** False when the key had already made this clone and the server answered with it again. */
  created: boolean;
  lines?: CloneOrderLine[];
  carrier: { carrier: string; carrierOrderId: string; trackingNumber: string; labelUrl?: string | null } | null;
  carrierError?: { code: string; message: string } | null;
}

/**
 * One order cloned from this one: as the source order's ORDER_CLONED event recorded it, or, for a clone V1 OMS
 * made, as the launch's V1 numbering recorded it (SSD-7397).
 */
export interface FulfillmentOrderClone {
  orderId: number;
  /** The clone's V1 OMS id (`455345-1`) when V1 made it; null for a clone made in Fulfillment 2.0. */
  v1OrderId?: string | null;
  /** Null for a V1 clone, whose numbering records no mode. */
  mode: CloneOrderMode | null;
  /** Null when the clone is not in Fulfillment 2.0. */
  fulfillmentStatus: string | null;
  createdAt: string | null;
  actor: string | null;
  reason: string | null;
}

/** Both ends of the clone link: the order this one was cloned from, and the orders cloned from it, oldest first. */
export interface FulfillmentOrderRelations {
  /** This order's own V1 OMS clone id when it is a V1 clone moved into Fulfillment 2.0 (SSD-7397). */
  v1OrderId?: string | null;
  cloneOf: FulfillmentOrderCloneSource | null;
  clones: FulfillmentOrderClone[];
}

/** The order this one was cloned from. */
export interface FulfillmentOrderCloneSource {
  orderId: number;
  rootOrderId: number;
  fulfillmentStatus: string | null;
  /** That order's V1 OMS clone id when it is itself a V1 clone (SSD-7397). */
  v1OrderId?: string | null;
  /** The carrier this clone was made for (SSD-7399); null when left to country routing, absent on an older server. */
  provider?: string | null;
}

/** Why the carrier order is being reissued; the server records it on both the cancel and the create event. */
export interface ResetCarrierOrderRequest {
  reason: string;
}

/** The carrier order that was cancelled, or null when there was none, and the one that replaced it. */
export interface ResetCarrierOrderResponse {
  cancelled: { carrier: string; carrierOrderId: string; trackingNumber: string } | null;
  created: CarrierOrderResponse;
}

/** The carriers a 2.0 switch may book, as the facade's ShippingProviderType names them. */
export type SwitchCarrierProvider = 'DELEO' | 'FEDEX' | 'PARXL' | 'YUN_EXPRESS';

/**
 * One switch of an order's carrier (SSD-7064). `expectedCarrierOrderId` is the carrier order the dialog
 * showed, so a source that changed underneath is refused; the key is one per dialog, and resending it
 * resumes the same switch rather than cancelling or booking twice.
 */
export interface SwitchCarrierOrderRequest {
  targetProvider: SwitchCarrierProvider;
  expectedCarrierOrderId: string;
  idempotencyKey: string;
  reason: string;
}

/** `replayed` means the key had already finished: no carrier was called and `labelUrl` is null. */
export interface SwitchCarrierOrderResponse {
  cancelled: { carrier: string; carrierOrderId: string; trackingNumber: string };
  created: CarrierOrderResponse;
  replayed: boolean;
}

/** `DECREASE` is a manual outbound or a count found short; `INCREASE` is a count found over. */
export type ManualStockDirection = 'DECREASE' | 'INCREASE';

/**
 * One manual outbound or correction at one shelf on the V2 ledger (SSD-7099). The key is one per change
 * the operator entered, so resending it after a lost answer replays rather than moving the stock twice.
 */
export interface AdjustStockRequest {
  sku: string;
  locationCode: string;
  direction: ManualStockDirection;
  qty: number;
  reason: string;
  idempotencyKey: string;
}

/** `delta` is negative for a decrease; `replayed` means the key had already booked it and nothing moved now. */
export interface AdjustStockResponse {
  sku: string;
  locationCode: string;
  delta: number;
  replayed: boolean;
}

/**
 * The 409 bodies the adjust route refuses with. `MANUAL_STOCK_BEFORE_LAUNCH` means V1 still holds the stock;
 * a shortfall names the scope (the shelf or the whole sku) that fell short, with that scope's figures.
 */
export type AdjustStockRefusal =
  | { code: 'MANUAL_STOCK_BEFORE_LAUNCH'; message: string }
  | {
      code: 'INSUFFICIENT_STOCK' | 'RESERVED_STOCK';
      message: string;
      sku: string;
      locationCode: string;
      scope: 'SHELF' | 'SKU';
      onHand: number;
      reserved: number;
    }
  | { code: 'LOCATION_OCCUPIED'; message: string; locationCode: string; occupiedBy: string };

/** Address fields stay `unknown`: the stored blobs are objects, not flat strings (core FulfillmentOrderEditPatch). */
export interface UpdateOrderAddressRequest {
  shippingAddress?: unknown;
  billingAddress?: unknown;
  consignee?: unknown;
  shippingMessage?: string;
}

export interface UpdateOrderAddressResponse {
  applied: boolean;
  /** Older address handlers return only applied; an explicit no-op takes precedence. */
  changed?: boolean;
}

export interface InboundLineRequest {
  locationCode: string;
}

export type ReceivedGoods = 'KEEP_IN_STOCK' | 'WRITE_OFF';

/** Per-line dispositions distinguish arrived stock write-offs from pre-arrival return or no-purchase intent. */
export interface CancelGoodsDisposition {
  lineNo: number;
  receivedGoods: ReceivedGoods | 'RETURN_TO_SELLER' | 'NOT_PURCHASED';
}

export interface CancelLineRequest {
  reason: string;
  receivedGoods?: ReceivedGoods;
}

/**
 * Whose reason an order cancel is (SSD-7450). A change of mind is the customer's and is refused once the order is
 * cancel-locked; COMPANY covers our side (out of stock, duplicate payment, cannot ship, fraud) and is never locked.
 */
export type OrderCancelType = 'CHANGE_OF_MIND' | 'COMPANY';

/** The cancel type, and for a change of mind when the customer asked, as an ISO UTC instant. */
export interface OrderCancelTypeRequest {
  cancelType: OrderCancelType;
  customerRequestedAt?: string;
  /** Why a change of mind asked after the lock is cancelled anyway (SSD-7643); the server posts it to the comments. */
  lockOverrideReason?: string;
}

/** A change-of-mind cancel asked for at or after the lock is refused with this code (HTTP 409). */
export const CANCEL_LOCKED_CODE = 'CANCEL_LOCKED';

/** restock true reverses the arrival off the balance; the location defaults to where the reservation put it. */
export interface CancelInboundLineRequest {
  restock: boolean;
  /** How many arrived units are kept; omitted keeps all of them, and the rest is written off. */
  restockQty?: number;
  locationCode?: string;
  /** Why the inbound is cancelled; kept on the cancel record and posted to the order's Comments. */
  memo?: string;
}

/** What the API accepts for a cancel-inbound memo. */
export const CANCEL_INBOUND_MEMO_LIMIT = 500;

export interface LineActionResponse {
  lineStatus: string;
  fulfillmentStatus: string;
}

/** What saving purchase provenance on a line answers; the pool rows its numbers closed ride along (SSD-7442). */
export interface LineAgentTrackingResponse {
  changed?: boolean;
  /** Absent on a backend that predates it: nothing is claimed then. */
  unrecognizedMatched?: UnrecognizedAutoMatch;
}

/** The unrecognized pool rows a line's saved numbers closed, and the shelves their boxes wait on. */
export interface UnrecognizedAutoMatch {
  rows: number;
  locations: string[];
}

export interface WrittenOffGoods {
  lineNo: number;
  sku: string;
  qty: number;
  locationCode: string;
}

/** What an order-level cancel did; cancelled=false means the order already read that way. */
export interface CancelFulfillmentOrderResponse {
  cancelled: boolean;
  cancelledLines: number;
  fulfillmentStatus: string;
  writtenOff?: WrittenOffGoods[];
}

export interface OutboundOrderResponse {
  fulfillmentStatus: string;
  trackingId: string;
}

export interface CancelOutboundOrderResponse {
  orderId: number;
  fulfillmentStatus: string;
  cancelledLines: number;
}

export type InboundRequestSource = 'WHOLESALE' | 'SMART_BUY' | 'PARTNERSHIP' | 'ETC';

export interface CreateInboundRequestLine {
  sku: string;
  qtyExpected: number;
  /** Required even when zero — a zero cost is a legitimate FOC line, not a missing value (FS-040). */
  unitCost: number;
  /** What the goods are expected to sell for; blank while nobody has priced them. */
  jitPrice?: number;
  isFoc?: boolean;
  /** Catalog wording captured when the line was picked; the desk reads it back rather than re-looking it up. */
  brand?: string;
  productName?: string;
}

/** supplier is required; the rest ride along in ops_doc.meta and come back on the detail doc. */
export interface CreateInboundRequestRequest {
  /** One key per Register attempt, reused on retry: the server answers a replay with the first request. */
  idempotencyKey?: string;
  source: InboundRequestSource;
  /** Where an OTHER-route arrival came from; the badge cannot say it. */
  otherChannel?: string;
  supplier: string;
  /** The purchase order the procurement sheet knows this arrival by; absent for non-purchases. */
  poNo?: string;
  invoiceNo?: string;
  /** The waybill the goods travel under; registering it here makes the request a live scan target at once. */
  tracking?: string;
  expectedAt?: string;
  memo?: string;
  lines: CreateInboundRequestLine[];
}

/** The correction an operator saves from the request form; an omitted field is left as it stands. */
export interface EditInboundRequestRequest {
  /** The row's `updatedAt` as the form read it; the server refuses the write if it has moved since. */
  expectedUpdatedAt?: string;
  source?: InboundRequestSource;
  supplier?: string;
  poNo?: string;
  tracking?: string;
  expectedAt?: string;
  memo?: string;
  lines?: CreateInboundRequestLine[];
}

/** One field the correction rewrote, as the audit log reads it back. */
export interface EditedInboundField {
  field: string;
  old: string | null;
  new: string | null;
}

/** What the correction settled — a save that changed nothing answers with no diff at all. */
export interface EditInboundRequestResponse {
  changed: boolean;
  diff: EditedInboundField[];
}

export interface CreateInboundRequestResponse {
  docId: string;
  inboundNo: string;
}

export interface InboundRequestSummary {
  docId: string;
  inboundNo: string;
  source: string;
  /** Present only on an OTHER-route request: the channel the operator typed. */
  otherChannel?: string;
  status: string;
  operator: string | null;
  tracking: string | null;
  /** Every waybill the request carries; one arrival can come in several parcels. Absent on a legacy response. */
  trackings?: string[];
  supplier?: string | null;
  poNo?: string | null;
  invoiceNo?: string | null;
  expectedAt?: string | null;
  memo?: string | null;
  /** Units the request declared. */
  expectedQty?: number;
  /** Units counted at the desk. Counting books nothing on its own — the explicit confirm still does [D-h]. */
  countedQty?: number;
  /** Units counted beyond what was expected, kept apart rather than folded into `countedQty` [D-h]. */
  overReceipt?: number;
  /** Why the request was cancelled, in the vocabulary the cancel modal offers. */
  cancelReason?: string | null;
  cancelMemo?: string | null;
  cancelledAt?: string | null;
  /** The instant the row last changed, which is what a stale-write check is compared against. */
  updatedAt?: string | null;
  /** Present only where the confirm settled short, carrying that confirm's own 부분 입고 N/M. */
  partialNote?: string;
  /** How many notes the request carries, so the row badges the thread without fetching it. */
  commentCount?: number;
  /** What the request is for, from its first line; null when it declares no lines yet. */
  sku?: string | null;
  brand?: string | null;
  productName?: string | null;
  lineCount?: number;
  /** When the arrival was confirmed; null while anything is still owed. */
  receivedAt?: string | null;
  createdAt: string | null;
}

/** Wire values the fulfillment API stores and validates against — not display copy. */
export const INBOUND_STATUS = {
  REQUESTED: '신청',
  PARTIAL: '부분입고',
  RECEIVED: '입고완료',
  CANCELLED: '취소',
} as const;

/** Lifecycle order, so the filter tabs and the footer read the way a request actually travels. */
export const INBOUND_STATUSES: string[] = [
  INBOUND_STATUS.REQUESTED,
  INBOUND_STATUS.PARTIAL,
  INBOUND_STATUS.RECEIVED,
  INBOUND_STATUS.CANCELLED,
];

const INBOUND_STATUS_LABELS: Record<string, string> = {
  [INBOUND_STATUS.REQUESTED]: 'Requested',
  [INBOUND_STATUS.PARTIAL]: 'Partial',
  [INBOUND_STATUS.RECEIVED]: 'Received',
  [INBOUND_STATUS.CANCELLED]: 'Cancelled',
};

/** Operators read English; the wire value stays whatever the API stored. */
export const inboundStatusLabel = (status: string): string => INBOUND_STATUS_LABELS[status] ?? status;

export interface ListInboundRequestsParams {
  status?: string;
  limit?: number;
  /** Where the next page starts; the ledger outgrows one page long before anyone reads to the end. */
  cursor?: string;
}

export interface ListInboundRequestsResponse {
  items: InboundRequestSummary[];
  /** Null or absent means this page is the end of the ledger. */
  nextCursor?: string | null;
  /** Rows matching the current status filter, or every status when unfiltered — not the page. */
  total?: number;
  /** Keyed by the status value itself — only INBOUND_STATUS values exist (FS-042). */
  footer: Record<string, number>;
}

export interface InboundRequestLine {
  lineNo: number;
  sku: string;
  /** Captured when the request was raised; a request older than the columns carries null. */
  brand: string | null;
  productName: string | null;
  qtyExpected: number;
  qtyReceived: number;
  unitCost: number | null;
  isFoc: boolean;
  locationCode: string | null;
  suggestedLocationCode: string | null;
  isNewSku: boolean;
  /** The place this line would be booked into is a bookkeeping code, not a shelf anyone can walk to. */
  landsOnVirtualLocation: boolean;
  /** When the row was last written; an expected-quantity edit carries it back so a stale correction is refused [T05]. */
  updatedAt?: string;
}

export interface InboundRequestTallies {
  expected: number;
  received: number;
  /** What is still owed, floored at zero — a shortfall is never cancelled out by a surplus elsewhere. */
  remaining: number;
  /** Units counted beyond what the lines asked for; absent from an older server, which never sent one. */
  over?: number;
}

/** One note on an arrival request; the key is what makes a retried save the same note. */
export interface InboundComment {
  text: string;
  author: string;
  at: string;
  idempotencyKey: string;
}

export interface InboundRequestDetail {
  doc: InboundRequestSummary;
  lines: InboundRequestLine[];
  tallies: InboundRequestTallies;
  comments: InboundComment[];
}

/** The note to append; the operator rides along because the API key authenticates the dashboard, not a person. */
export interface AppendInboundCommentRequest {
  text: string;
  idempotencyKey: string;
}

export interface AttachInboundTrackingRequest {
  /** One key per attach attempt, reused on retry, so a double submit leaves one registration record. */
  idempotencyKey?: string;
  tracking?: string;
  bulk?: string[];
}

export interface CountScanInboundRequest {
  barcode?: string;
  sku?: string;
}

/** Why an expectation was rewritten; the desk picks one and cannot invent a fourth [L-M6]. */
export type InboundExpectedEditReason = 'DAMAGED_DEFECTIVE' | 'SUPPLIER_QTY_CHANGE' | 'OTHER';

export interface EditInboundLineQtyRequest {
  qty: number;
  /** Required on the expected edit; the received edit is `EditInboundLineReceivedRequest` and carries none. */
  reason?: InboundExpectedEditReason;
  memo?: string;
  /** The line's `updatedAt` as the desk read it; the server refuses a count typed against a moved row [T05]. */
  expectedUpdatedAt?: string;
}

/**
 * The received edit adds to the server's running total instead of replacing it, so two stations
 * counting the same line accumulate (2 then 3 makes 5). Negative corrects downward; the server refuses below 0.
 */
export interface EditInboundLineReceivedRequest {
  delta: number;
}

export interface EditInboundLineReceivedResponse {
  sku: string;
  lineNo: number;
  qtyReceived: number;
  /** The request's status after the edit: a received request a count was taken out of is open again. */
  status?: string;
  /** Units this edit took back out of stock (a received line counted lower than what was booked). */
  uncounted?: number;
  /** Of those, how many an order's reservation was counting on: those orders are now short. */
  reservedShort?: number;
}

export interface EditInboundLineExpectedResponse {
  sku: string;
  lineNo: number;
  qtyExpected: number;
  /** The request's status after the edit: a received request that no longer matches is open again. */
  status?: string;
}

export interface CountScanInboundResponse {
  sku: string;
  lineNo: number;
  qtyReceived: number;
}

export interface InboundLocationAssignment {
  sku: string;
  locationCode: string;
}

export interface AssignInboundLocationsRequest {
  assignments: InboundLocationAssignment[];
}

export interface ConfirmInboundRequest {
  mode: 'FULL' | 'PARTIAL';
  reason?: string;
}

export interface ConfirmInboundResponse {
  status: string;
}

/** A cancellation always says why; the memo is what makes 기타 mean something. */
export interface CancelInboundRequestBody {
  reason: string;
  memo?: string;
}

export interface ReadyToOutboundLine {
  lineNo: number;
  sku: string;
  /** Server-resolved KO → EN → sku, so the screen never falls back itself. */
  productName: string;
  qty: number;
  sourcingRoute: string;
  pickLocation: string | null;
}

export interface ReadyToOutboundOrder {
  orderId: number;
  wcStatus: string | null;
  hasJit: boolean;
  isMarketing: boolean;
  /** Conditions the order does not meet; a marketing order queues anyway and only carries them. */
  violations: string[];
  lines: ReadyToOutboundLine[];
  /* The server sends an explicit null where it has no answer, so these say null rather than only absent. */
  /** ISO 8601; the queue screen renders it ko-KR. */
  orderDate?: string | null;
  /** The influencer handle on a marketing order. */
  customerName?: string | null;
  /** Person in charge, read under the customer on marketing rows. */
  pic?: string | null;
  /** Badge count only; the panel itself loads through listOrderComments. */
  commentCount?: number | null;
  /** How much of the order has arrived, from the one predicate the outbound gate also reads [T04]. */
  inboundState?: string;
}

/** The concrete band an order queues under; the All tab lists them inv, then mkt, then jit. */
export type ReadyToOutboundGroup = 'inv' | 'mkt' | 'jit';

export interface ListReadyToOutboundParams {
  tab?: 'all' | ReadyToOutboundGroup;
  limit?: number;
  beforeOrderId?: number;
  /** The previous page's nextCursorGroup, so the All tab resumes inside the band it stopped in. */
  cursorGroup?: ReadyToOutboundGroup;
}

/** Whole-queue candidate counts, before the per-page live WooCommerce check. */
export interface ReadyToOutboundTotals {
  all: number;
  inv: number;
  mkt: number;
  jit: number;
}

export interface ListReadyToOutboundResponse {
  /** Already gated and sorted by the server — JIT orders arrive last. */
  orders: ReadyToOutboundOrder[];
  nextCursor: number | null;
  /** The band of the page's last order; absent on a server that pages by order id alone. */
  nextCursorGroup?: ReadyToOutboundGroup | null;
  /** The server answered from a stale read; the queue is usable but may be a moment behind. */
  liveStatusDegraded?: boolean;
  /** Absent on a server that predates them, which leaves the tabs counting the rows on screen. */
  totals?: ReadyToOutboundTotals;
}

export interface PickListPick {
  /** null means the sku is on no shelf — the server sends it last so the picker still hunts for it. */
  location: string | null;
  sku: string;
  productName: string;
  qty: number;
  orderId: number;
  lineNo: number;
  /** The sample set rides the walk like merchandise; the sheet marks it apart. */
  isSample?: boolean;
  /** The last four digits of the product's barcode; absent on a server that predates it. */
  eanLast4?: string | null;
  /** The product's size, as the PACKING sheet prints it; absent on a server that predates it. */
  size?: string | null;
  /**
   * The whole line's locations. A line split across shelves arrives as one pick per shelf, `location` and
   * `qty` being that shelf's share; absent on a server that predates it.
   */
  locations?: FulfillmentLocationQty[];
}

export interface PickListLabel {
  orderId: number;
  lineCount: number;
  totalQty: number;
}

export interface PickListResponse {
  /** Pre-sorted into walk order by the server. */
  picks: PickListPick[];
  labels: PickListLabel[];
}

export interface BulkOutboundRequest {
  orderIds: number[];
  locationCode?: string;
}

export interface BulkOutboundApplied {
  orderId: number;
  fulfillmentStatus: string;
  trackingId: string;
}

/** An order that had already shipped, so the run left it alone; not a refusal and not an error. */
export interface BulkOutboundSkipped extends BulkOutboundApplied {
  reason: string;
}

export interface BulkOutboundFailure {
  orderId: number;
  /** One sentence per order for the result panel; the violations are the same thing itemised. */
  reason: string;
  violations: string[];
}

/** Shipped now, already shipped, refused — the three outcomes stay apart so a partial run reads true [D-q]. */
export interface BulkOutboundResponse {
  applied: BulkOutboundApplied[];
  skipped: BulkOutboundSkipped[];
  failed: BulkOutboundFailure[];
}

export interface ScanCodeRequest {
  code: string;
}

/** One further thing the same code turned out to be, when the dispatcher could not pick between them. */
/** One further thing the code names, in a runner-up's shorter vocabulary: `ORDER`, not `ORDER_WAYBILL`. */
export interface ScanCodeMatch {
  kind: 'ORDER' | 'INBOUND_REQUEST' | 'LINE' | 'UNRECOGNIZED';
  orderId?: number;
  trackingNumber?: string;
  docId?: string;
  inboundNo?: string;
  sku?: string;
}

export interface ScanCodeResponse {
  kind: 'ORDER_WAYBILL' | 'RETURN_WAYBILL' | 'INBOUND_REQUEST' | 'PRODUCT_BARCODE' | 'UNKNOWN';
  /** Everything else the code matched; present only when the dispatcher had to choose and would not. */
  alsoMatched?: ScanCodeMatch[];
  orderId?: number;
  /** Set on RETURN_WAYBILL: the customer-facing number read off the parcel that came back. */
  trackingNumber?: string;
  docId?: string;
  inboundNo?: string;
  /** Set on PRODUCT_BARCODE: the catalog placed the code even though no order carries it. */
  sku?: string;
  packSize?: number;
  /** KO name, else EN, else null — the sku is the code just scanned, so it names nothing on its own. */
  productName?: string | null;
  /** Set on UNKNOWN: the pool entry the miss parked, so describing the parcel fills that row rather than adding a second. */
  parkedScanId?: string;
  /**
   * Set on ORDER_WAYBILL when the same box is still parked in the unrecognized pool (SSD-7442): its waybill was
   * registered on an order after the desk parked it, so the desk can close it on the order the scan just opened.
   */
  poolParcel?: ScanPoolParcel;
}

/** A parcel still open in the unrecognized pool under the code just scanned. */
export interface ScanPoolParcel {
  parcelId: string;
  /** Where the desk left the box when it parked it; null when it said nowhere. */
  temporaryLocation: string | null;
  rows: ScanPoolParcelRow[];
}

/** One product of that parcel and the line of the opened order it fits; the line fields are null when none fits. */
export interface ScanPoolParcelRow {
  id: string;
  sku: string | null;
  quantity: number | null;
  orderId: number | null;
  lineNo: number | null;
  /** The sku closing the row resolves it to: the line's own. */
  lineSku?: string | null;
}

export interface UpdateTemporaryLocationRequest {
  value: string | null;
}

export interface BulkInboundRequest {
  lineNos?: number[];
  all?: boolean;
  locationCode: string;
}

export interface BulkInboundFailure {
  lineNo: number;
  reason: string;
}

export interface BulkInboundResponse {
  inbounded: number;
  fulfillmentStatus: string;
  /** Lines the run passed over rather than refused — already in, so nothing was wrong with them. */
  skipped?: BulkInboundFailure[];
  failures?: BulkInboundFailure[];
}

export interface ClosingSession {
  docId: string;
  status: string;
  operator: string | null;
  /** The manually counted packed boxes the scan run has to reconcile against (FS-097 gate). */
  expectedCount: number | null;
  /** Which confirmed version stands: 0 before the first confirm, 2 and up once amended. */
  version?: number;
  startedAt: string | null;
  confirmedAt: string | null;
  signedOffBy: string | null;
}

export interface ClosingFirstScan {
  lineNo: number;
  at: string | null;
  operator: string | null;
}

export interface ClosingScanLine {
  lineNo: number;
  boxSeq: number;
  waybill: string;
  verdict: string;
  orderId: number | null;
  fulfillmentStatus: string | null;
  totalQuantity: number | null;
  warning: string | null;
  scannedAt: string | null;
  operator: string | null;
  /** Absent until the server persists the first scan onto the line; a duplicate row reads without it. */
  firstScan?: ClosingFirstScan | null;
}

export interface ClosingTallies {
  scanned: number;
  ok: number;
  warnings: number;
  duplicates: number;
  unknown: number;
  /** Clean boxes the manual count still expects: max(0, expectedCount − ok) [D-f]. */
  remaining: number;
  /** Clean boxes past the manual count: max(0, ok − expectedCount). Only ever one of the pair is set [D-f]. */
  overScanned?: number;
}

/** The tallies frozen at confirmation; each amendment freezes the next version over it. */
export interface ClosingSnapshot extends ClosingTallies {
  version: number;
  expectedCount: number;
  confirmedAt: string;
  signedOffBy: string;
  /** Whether the operator confirmed over standing warnings rather than clearing them. */
  forced: boolean;
}

export interface ClosingSessionDetail {
  session: ClosingSession;
  lines: ClosingScanLine[];
  /** A live recount of the log as it stands; a confirmed day is read from the snapshot instead. */
  tallies: ClosingTallies;
  /** What the confirm froze, or null on a session that has never been confirmed. */
  snapshot?: ClosingSnapshot | null;
}

/**
 * One History row: the session plus its warning record. Older servers send none of the warning fields,
 * so each is optional and a missing one reads as unknown, never as zero.
 */
export interface ClosingSessionHistoryRow extends ClosingSession {
  /** Blocking warnings the scan log ever raised, including ones since resolved or removed. */
  warningsRaised?: number;
  /** Raised warnings that were resolved in place or removed from the log. */
  warningsCleared?: number;
  /** Warnings standing now; for a confirmed session, the ones it was signed off over. */
  warningsStanding?: number;
  /** The standing confirmation was signed off despite warnings. */
  forced?: boolean;
}

export interface ClosingSessionListResponse {
  sessions: ClosingSessionHistoryRow[];
}

export interface StartClosingRequest {
  expectedCount: number;
}

export interface DeleteClosingScanResponse {
  deleted: boolean;
}

export interface StartClosingResponse {
  docId: string;
  startedAt: string;
}

export interface ScanClosingRequest {
  waybill: string;
}

export interface ScanClosingResponse {
  lineNo: number;
  verdict: string;
  orderId?: number;
  fulfillmentStatus?: string;
  totalQuantity?: number;
  firstScan?: ClosingFirstScan;
}

export interface EditClosingTargetRequest {
  expectedCount: number;
  /** The target the editor read; the server refuses the edit with 409 CONFLICT once it has moved. */
  previousExpectedCount?: number;
  /** The session `version` the editor read; a re-confirm in between refuses the edit the same way. */
  expectedVersion?: number;
}

/** The 409 a stale target edit answers with: the session as it stands now, so the screen can redraw it. */
export interface ClosingTargetConflict {
  code: 'CONFLICT';
  message?: string;
  current: Pick<ClosingSession, 'docId' | 'status' | 'expectedCount'> & { version: number };
  /** Who moved the target, re-confirmed or closed the session, when the server recorded a name. */
  changedBy?: string;
}

export interface EditClosingTargetResponse {
  expectedCount: number;
  previousExpectedCount: number;
}

export interface ClosingAmendmentResponse {
  docId: string;
  status: string;
  /** The confirmed version the amendment works from; re-confirming writes the next one. */
  version: number;
}

export interface ProcessClosingScanOrderRequest {
  zeroPackingVerified: boolean;
  /** Owns the row's irreversible outbound on the server; every retry of the same row must resend it. */
  idempotencyKey: string;
  memo?: string;
}

export interface ProcessClosingScanOrderResponse {
  lineNo: number;
  verdict: string;
  orderId: number;
  fulfillmentStatus: string;
  /** True when the order had already left; nothing moved and the row was only re-judged. */
  alreadyOutbounded: boolean;
}

export interface CancelClosingResponse {
  status: string;
  /** Live scans dropped from the working count; the rows themselves stay on the cancelled session. */
  discardedScans: number;
  retainedLineNos: number[];
}

export interface ConfirmClosingRequest {
  force?: boolean;
}

export interface ConfirmClosingResponse {
  status: string;
  snapshot: unknown;
}

export interface InventoryRow {
  sku: string;
  locationCode: string;
  onHand: number;
  reserved: number;
  available: number;
  /** Required on the wire; optional only until the server deploys — drop the `?` once both sides are on dev. */
  pickable?: boolean;
  /** Every route that stocked this shelf, so an older server's row reads as unsourced rather than as some default. */
  sourcingRoutes?: string[];
  /** The server's own integrity readings; absent on a server that predates them, so the screen still derives its own. */
  reservedExceedsTotal?: boolean;
  negativeAvailable?: boolean;
}

export interface ReservationHolder {
  orderId: number;
  lineNo: number;
  qty: number;
  /** The shelf the reservation sits on; null for an ON_HAND reservation pinned to no shelf, absent on an older server. */
  locationCode?: string | null;
}

export interface StockLedgerRow {
  movementType: string;
  delta: number;
  locationCode: string;
  occurredAt: string;
  actor: string | null;
  refType: string | null;
  refId: string | null;
  /** The parcel number that matched a tracking search, when the movement belongs to one. */
  trackingNumber: string | null;
  /** A barcode, order, waybill or name search answers across skus, so every row names its own [SSD-6456]. */
  sku: string;
  /** The catalog's labels for that sku; both null for a sku the catalog no longer carries. */
  productName: string | null;
  productNameKr: string | null;
  /**
   * What booked the row when its movement type alone cannot say: a manual adjust, a stock audit or the
   * launch opening balance on AUDIT_ADJUST, a reservation release on INBOUND_CANCEL. Null on every other row and on rows booked
   * before the server recorded it; absent on an older server.
   */
  origin?: StockLedgerOrigin | null;
  /** The count reason or the release reason; null when none was recorded. */
  reason?: string | null;
  /** The shelf's on-hand just before and after the row; null on rows booked before the server recorded it. */
  onHandBefore?: number | null;
  onHandAfter?: number | null;
  /** Only an inbound under an order or an inbound request is routed; every other row reads UNKNOWN. */
  sourcingRoute?: string | null;
}

/** The ledger's origin vocabulary; manual adjust and stock audit both book AUDIT_ADJUST. */
export type StockLedgerOrigin = 'MANUAL_ADJUST' | 'STOCK_AUDIT' | 'RESERVATION_RELEASE' | 'LAUNCH_OPENING_BALANCE';

/** What a stock-take round is for: the inventory ✎ edit opens a MANUAL_ADJUST round, a walk a STOCK_AUDIT one. */
export type StockTakeOrigin = 'MANUAL_ADJUST' | 'STOCK_AUDIT';

/** The vocabulary the ledger filters accept, sent on every response so the screen never hard-codes it. */
export interface StockLedgerFilters {
  movementTypes: string[];
  origins: string[];
  sourcingRoutes: string[];
}

export interface ListInventoryParams {
  skus?: string[];
  limit?: number;
  /** The server withholds rows at zero by default; ask for them back when the operator wants to see them. */
  includeEmpty?: boolean;
  /** Continue a paged shelf map when the server supports it. */
  cursor?: string;
}

export interface InventoryPage {
  rows: InventoryRow[];
  nextCursor?: string | null;
}

/**
 * The five keys Stock History searches on [SSD-6456 / QA-HIS-13]. `SKU`, `BARCODE`, `ORDER_ID` and
 * `TRACKING_NO` match the whole value; `PRODUCT_NAME` matches part of the name, case-insensitively,
 * across the English and the Korean name.
 */
export type StockLedgerSearchField = 'SKU' | 'BARCODE' | 'ORDER_ID' | 'TRACKING_NO' | 'PRODUCT_NAME';

/**
 * Either the original exact-sku form or the explicit pair. The server refuses a bare term, half a
 * pair, or both forms at once with a 400, so the two shapes are kept mutually exclusive here.
 */
export type StockLedgerQuery = { sku: string } | { searchField: StockLedgerSearchField; keyword: string };

export type GetStockLedgerParams = StockLedgerQuery & {
  movementType?: string;
  origin?: StockLedgerOrigin;
  limit?: number;
  /** A previous response's nextCursor, to read the next older page of the same search; omit for the newest page. */
  cursor?: string;
};

export interface StockLedgerResponse {
  rows: StockLedgerRow[];
  /** True when a page filled its limit, so a cut-off answer is not read as the whole history. */
  truncated?: boolean;
  /** Reads the next older page when this one filled its limit; null on the last page, absent on an older server. */
  nextCursor?: string | null;
  /** Absent on an older server, which takes no origin filter. */
  filters?: StockLedgerFilters;
}

export interface ReleaseReservationRequest {
  orderId: number;
  lineNo: number;
  reason: string;
  /** Whether the freed units go back on the shelf or are written off — the two are not the same book entry. */
  restock: boolean;
  restockQty?: number;
  locationCode?: string;
}

/** Stock move payload with a caller-stable key for safe retries. */
export interface MoveStockRequest {
  sku: string;
  fromLocation: string;
  toLocation: string;
  qty: number;
  idempotencyKey: string;
}

export interface ReturnPutawayRequest {
  /** One key per put-away attempt, reused on retry; a different key on an already-returned line is refused. */
  idempotencyKey?: string;
  lineNo: number;
  locationCode: string;
  /** How many units came back; the whole line when the desk does not say [L-M3]. */
  qty?: number;
  memo?: string;
}

/** Opens one location-scoped stock take with a caller-stable retry key. */
export interface OpenStockTakeRequest {
  locationCode: string;
  idempotencyKey: string;
  /** Ties every shelf of one multi-shelf walk together, so a refresh can find the whole session again. */
  auditGroupId?: string;
  /** The shelf's frozen target set; widening the filter mid-walk must not grow what was opened. */
  targetSkus?: string[];
  /** Omitted reads as STOCK_AUDIT on the server; MANUAL_ADJUST cannot be combined with an auditGroupId. */
  origin?: StockTakeOrigin;
}

export interface CountStockTakeRequest {
  sku: string;
  countedQty: number;
  /** Optional note for this recorded count; the server stores it on the line and accepts 1–200 characters. */
  reason?: string;
  /** The shelf balance the editor showed; the server refuses the count with 409 STALE_STOCK once it has moved. */
  expectedSystemQty?: number;
}

/** One SKU whose shelf balance moved after it was counted (or after the editor read it). */
export interface StaleStockLine {
  sku: string;
  countedAgainst: number;
  current: number;
  countedQty: number;
  /** Absent when the SKU has no saved line yet. */
  lineNo?: number;
  /** Who made the shelf's newest movement on this SKU, when the ledger recorded a name. */
  changedBy?: string;
}

/** The 409 a stale count or confirm answers with; nothing was written or adjusted, and the round stays open. */
export interface StaleStockRefusal {
  code: 'STALE_STOCK';
  message?: string;
  docId: string;
  staleLines: StaleStockLine[];
}

/** The 409 a count or confirm answers with once a colleague confirmed or abandoned the round; nothing was written. */
export interface ClosedStockTakeRefusal {
  code: 'STOCK_TAKE_CLOSED';
  /** The server's own sentence, written for the desk: what happened to the round and who did it. */
  message: string;
  docId: string;
  status: string;
  closedBy?: string;
}

export interface StockTakeLine {
  lineNo: number;
  sku: string;
  systemQty: number;
  countedQty: number;
  diff: number;
  /** The note the counter left with this line; absent on a line counted without one or on a server that predates it. */
  reason?: string | null;
}

/** A round as it reads in the history list — the count itself lives in `StockTakeDetail`. */
export interface StockTakeRound {
  docId: string;
  status: string;
  /** Always a shelf: a round stored before the shelf was recorded reports the `WH#00` its lines adjust. */
  locationCode: string;
  /** Rounds opened before the writer was recorded carry none of these; the screen renders missing as missing. */
  operator: string | null;
  startedAt: string | null;
  confirmedAt: string | null;
  signedOffBy: string | null;
  /** Lines the round adjusted at confirm: zero while it is open, and zero forever once it is abandoned. */
  adjusted: number;
  /** The multi-shelf walk this round belongs to; absent on a round opened one shelf at a time. */
  auditGroupId?: string;
  /** The sku set frozen when this round opened; absent on rounds created before stock-audit sessions. */
  targetSkus?: string[];
  /** What the round was: a ✎ edit (MANUAL_ADJUST) or an audit walk (STOCK_AUDIT); absent on a server that predates it. */
  origin?: StockTakeOrigin;
}

/** Narrowing is the server's job: the screen shows the rounds it was handed, never a subset it kept back. */
export interface ListStockTakesParams {
  status?: string;
  locationCode?: string;
  limit?: number;
  /** One kind of round only; omitted lists both. */
  origin?: StockTakeOrigin;
  /** The `nextCursor` of the page before, to read the rounds after it. */
  before?: string;
}

export interface StockTakeListResponse {
  rounds: StockTakeRound[];
  /** Continues the list; null (or absent on an older server) once the last round has been served. */
  nextCursor?: string | null;
}

/** What a walked-away round leaves behind: no line was applied, so the only record is who closed it and why. */
export interface StockTakeCancelled {
  docId: string;
  status: string;
  cancelledAt: string;
  cancelledBy: string;
  reason: string | null;
}

/** Nothing on a stock take carries a unit cost, so the sheet counts lines and never values them. */
export interface StockTakeTallies {
  counted: number;
  discrepancies: number;
}

export interface StockTakeDetail {
  doc: Record<string, unknown> | null;
  lines: StockTakeLine[];
  tallies: StockTakeTallies;
}

/** A live order line carrying the parked barcode — the pool's proposal of where the scan belongs [BR-2]. */
export interface UnrecognizedScanSuspectedOrder {
  orderId: number;
  wcStatus: string | null;
  lineNo: number;
  sku: string;
  /** Who owns the candidate order — the person the handler confirms with, absent on an order with no PIC. */
  pic?: string | null;
  /** Where the candidate order came from; what tells a Naver order from an official-mall one at a glance. */
  channel?: string | null;
  /** What the warehouse did with the order, which is the state a match has to be judged against. */
  fulfillmentStatus?: string | null;
  /** How many units the candidate line wants — never the number of units in the operator's hand. */
  lineQty?: number | null;
}

export interface UnrecognizedScan {
  id: string;
  code: string;
  operator: string | null;
  occurredAt: string;
  /** What the desk declared about the parcel; null on a code the scanner parked on its own. */
  trackingNo?: string | null;
  orderNo?: string | null;
  sku?: string | null;
  quantity?: number | null;
  memo?: string | null;
  /**
   * The parcel this row belongs to: the rows of one box share it, and a row parked before parcels existed stands
   * as a parcel of one under its own id. Absent on a backend that predates parcels, which reads the same way.
   */
  parcelId?: string;
  /** What the desk said the scanned code was — WAYBILL or PRODUCT; null or absent when it never said. */
  codeKind?: string | null;
  /** The row's place among the products of its parcel, 1-based; null on a row from before parcels existed. */
  itemNo?: number | null;
  /** The catalog's reading of the declared sku, so the pool reads as goods rather than as a number. */
  brand?: string | null;
  productName?: string | null;
  productNameKr?: string | null;
  size?: string | null;
  /** Absent on a backend that predates the candidates; an empty array means nothing carries the code. */
  suspectedOrders?: UnrecognizedScanSuspectedOrder[];
  /**
   * Every live order line the entry could belong to, before the server caps `suspectedOrders` at a few:
   * what says how many were left out. Absent on a backend that predates it, so nothing is claimed then.
   */
  suspectedOrdersTotal?: number;
  /** Where the desk put the box while it waits (SSD-7442); null or absent when it said nowhere. */
  temporaryLocation?: string | null;
  /** The parcel's thread in the order and FC teams' channel; null or absent before it was announced. */
  slackThreadUrl?: string | null;
  /**
   * Where the parcel's own numbers point now that someone registered them on an order line (SSD-7442); null or
   * absent when they point nowhere yet.
   */
  numberMatch?: UnrecognizedScanNumberMatch | null;
}

/** A parked row's numbers, read against the order lines they have since been registered on. */
export interface UnrecognizedScanNumberMatch {
  /** MATCHED: one live line carries this product; MULTIPLE: several do; PRODUCT_DIFFERS: none does; ORDER_CLOSED: every line is finished. */
  status: 'MATCHED' | 'MULTIPLE' | 'PRODUCT_DIFFERS' | 'ORDER_CLOSED';
  matchedBy: 'PURCHASE_ORDER' | 'WAYBILL';
  orderId: number | null;
  lineNo: number | null;
  /** That line's sku, which a match onto it resolves the row to. */
  sku?: string | null;
  lineCount: number;
}

/** One product the desk read in the parcel; no sku is a product it could not name. */
export interface RegisterUnrecognizedScanItem {
  sku?: string;
  quantity: number;
}

/** What the View Orders desk could read off a parcel whose barcode the catalog would not place. */
export interface RegisterUnrecognizedScanRequest {
  code: string;
  /** How many units, when the body names one product or none; `items` carries the counts otherwise. */
  quantity?: number;
  /** Every product in the parcel with its count; the server parks one row per product under one parcel id. */
  items?: RegisterUnrecognizedScanItem[];
  /** What the desk says the scanned code was; a WAYBILL is never read against the catalog. */
  codeKind?: 'WAYBILL' | 'PRODUCT';
  /** The entry the dead-end scan already parked; describing it keeps one parcel to one row. */
  scanId?: string;
  trackingNo?: string;
  orderNo?: string;
  sku?: string;
  memo?: string;
  /** Where the desk put the box while it waits for an owner, as a shelf code (SSD-7442); optional. */
  temporaryLocation?: string;
  /**
   * Parks a code the catalog cannot place. The server refuses one by default so a mistyped scan does
   * not become a pool row; the desk sets this when it has looked and the parcel is real anyway.
   */
  force?: boolean;
}

/** Acknowledges a parked parcel; the id is what the pool screen works the entry by. */
/** One line a purchase order number placed, as the M2 lookup table renders it. */
export interface PurchaseOrderCandidate {
  orderId: number;
  lineNo: number;
  sku: string;
  brand: string | null;
  productName: string | null;
  productNameKr: string | null;
  quantity: number;
  /** The tracking the line already carries; such a row must not be registered a second time. */
  trackingNumber: string | null;
  /** The catalog thumbnail the desk matches the box against; null when the snapshot carried none. */
  imageUrl: string | null;
}

/** The M2 order-number lookup: the number as searched, and every line it placed. */
export interface PurchaseOrderLookup {
  orderNo: string;
  candidates: PurchaseOrderCandidate[];
}

export interface UnrecognizedScanRegistered {
  id: string;
  /** The parcel was already in the pool and this call answered with the rows it already holds [T11]. */
  reused?: boolean;
  /** The parcel every product of this registration was parked under; absent on a backend that predates parcels. */
  parcelId?: string;
  /** One entry per product named, each with the row it landed on; absent on a backend that predates parcels. */
  items?: Array<{ id: string; sku: string | null; quantity: number; reused: boolean }>;
}

export interface UnrecognizedScanPool {
  /** Every row of every parcel on the page, parcel by parcel: a page never cuts a parcel in two. */
  scans: UnrecognizedScan[];
  /** Every open entry (one per product), not just the page — absent on a backend that predates it, so the screen must read fine without it. */
  openTotal?: number;
  /** Every open parcel, not just the page — absent on a backend that predates parcels. */
  openParcels?: number;
  /** The candidate lookup failed rather than found nothing; an empty candidate list then means nothing. */
  suspectedOrdersUnavailable?: boolean;
  /** Where the next page starts; null or absent means this page is the end of the pool. */
  nextCursor?: string | null;
}

/** One page of the pool: the cap is the server's, and the cursor is how the rest of it is reached. */
export interface ListUnrecognizedScansParams {
  limit?: number;
  cursor?: string;
}

/**
 * One page of every order line a parked entry could belong to (SSD-7208). The pool row proposes only a
 * handful; this has no cap beyond the page, newest order first, so the candidate the handful left out is reachable.
 */
export interface UnrecognizedScanCandidatePage {
  suspectedOrders: UnrecognizedScanSuspectedOrder[];
  /** Every candidate across all pages — the same count the pool row states as `suspectedOrdersTotal`. */
  suspectedOrdersTotal: number;
  /** Where the next page continues; null or absent once every candidate has been served. */
  nextCursor?: string | null;
}

/** The product line a match resolves against — it is what lets the backend write tracking onto that line [BR-5]. */
export interface ResolveUnrecognizedScanLine {
  orderId: number;
  lineNo: number;
}

/** Resolving by a hand-typed sku names no line; the `never`s keep half a reference unrepresentable. */
export interface ResolveUnrecognizedScanWithoutLine {
  orderId?: never;
  lineNo?: never;
}

export interface ResolveUnrecognizedScanFields {
  sku: string;
  /** Where the match was made, for the FC team's thread (SSD-7442); the pool screen when omitted. */
  via?: 'POOL' | 'DESK_SCAN';
}

export type ResolveUnrecognizedScanRequest = ResolveUnrecognizedScanFields &
  (ResolveUnrecognizedScanLine | ResolveUnrecognizedScanWithoutLine);

/** A closed vocabulary [BR-8/9/10]: the wire carries the constant, which outlives a rewording of the label. */
export const UNRECOGNIZED_SCAN_REMOVAL_REASONS = [
  'REGISTERED_BY_MISTAKE',
  'ROUTED_TO_INBOUND_REQUEST',
  'NO_ACTION_NEEDED',
  'OTHER',
] as const;

export type UnrecognizedScanRemovalReason = (typeof UNRECOGNIZED_SCAN_REMOVAL_REASONS)[number];

/**
 * The exact wording the screen renders for each reason; the mapping is this table and nothing else,
 * mirroring the backend's FULFILLMENT_UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS.
 */
export const UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS: Record<UnrecognizedScanRemovalReason, string> = {
  REGISTERED_BY_MISTAKE: 'Registered by mistake',
  ROUTED_TO_INBOUND_REQUEST: 'Routed to inbound request',
  NO_ACTION_NEEDED: 'No action needed',
  OTHER: 'Other',
};

/** The reason that names nothing on its own, so the screen demands the sentence beside it. */
export const UNRECOGNIZED_SCAN_MEMO_REASON: UnrecognizedScanRemovalReason = 'OTHER';

/** The reason that names a document, so the screen knows which one to prompt an inbound number for. */
export const UNRECOGNIZED_SCAN_INBOUND_REASON: UnrecognizedScanRemovalReason = 'ROUTED_TO_INBOUND_REQUEST';

/**
 * An arrival number is an 8-digit day stamp followed by that day's sequence, so the field holds the
 * operator to the width the API accepts rather than letting a typo travel as far as a 400. The
 * sequence is not fixed at four digits, so the shape spans 12 to 20 digits as the wire does.
 */
export const UNRECOGNIZED_SCAN_INBOUND_NO_PATTERN = /^\d{12,20}$/;

/** The widest that same number goes, so the input cannot outrun what the pattern will accept. */
export const UNRECOGNIZED_SCAN_INBOUND_NO_LIMIT = 20;

/** What the API accepts for a removal memo. */
export const UNRECOGNIZED_SCAN_MEMO_LIMIT = 500;

/** The reason is mandatory; the entry is kept, not deleted [BR-8/9/10]. */
export interface RemoveUnrecognizedScanRequest {
  reason: UnrecognizedScanRemovalReason;
  /** Where the goods went when the reason routes them on — prompted, never forced [BR-9]. */
  inboundNo?: string;
  /** Why in words; prompted on every reason and demanded behind the one that names nothing. */
  memo?: string;
}

export interface MarketingImportRow {
  campaignId: string;
  influencerId: string;
  /** Optional since SSD-7448: the batch PIC is what the order records. */
  owner?: string;
  sku: string;
  qty: number;
  recipientName: string;
  recipientPhone: string;
  addressLine1: string;
  city: string;
  countryCode: string;
  /** Absent only for a country with no postal code. */
  postcode?: string;
  recipientEmail?: string;
  addressLine2?: string;
  /** Required for US, CA and AU; the server answers a US or CA state as its two-letter code. */
  state?: string;
}

/** How much a template column has to be filled: always, only for some countries, or never. */
export type MarketingImportColumnNeed = 'required' | 'conditional' | 'optional';

/** One column of the marketing import template as the server explains it (SSD-7448). */
export interface MarketingImportColumn {
  name: string;
  need: MarketingImportColumnNeed;
  note: string;
}

/** The template header and, from servers that send it, what each column is for. */
export interface MarketingImportTemplate {
  template: string;
  columns?: MarketingImportColumn[];
}

/** A previewed row with the carrier its country is connected to; null means nobody carries it. */
/**
 * A previewed row with the two answers the file cannot give: the product behind the sku, and the
 * carrier serving its country. An unnamed product is a sku the catalog does not know.
 */
export interface MarketingImportPreviewRow extends MarketingImportRow {
  productName: string | null;
  carrier: string | null;
}

/** One row and why it cannot be imported, so the reason sits on the row rather than in a list below it. */
export interface MarketingImportRowError {
  row: number;
  message: string;
}

/** A country no carrier serves, named as well as coded — a two-letter code is not a country to most people. */
export interface MarketingImportBlockedCountry {
  code: string;
  name: string;
}

/** What the preview folded together: several file rows become one order per recipient and address. */
export interface MarketingImportMergeNotice {
  rows: number;
  orders: number;
}

export interface MarketingImportPreview {
  rows: MarketingImportPreviewRow[];
  /** Why each refused row was refused, keyed by its 1-based place in the file. */
  rowErrors: MarketingImportRowError[];
  /** Countries in this file that no carrier serves; a confirm is refused while any remain. */
  blockedCountries: MarketingImportBlockedCountry[];
  /** Present when the file's rows do not map one-to-one onto orders. */
  mergeNotice?: MarketingImportMergeNotice;
  /** SHA-256 of the uploaded file; the confirm carries it back so a re-upload can be recognised. */
  fileHash: string;
}

/** One country and the carrier that serves it. */
export interface CarrierConnection {
  countryCode: string;
  carrier: string;
}

export interface CarrierRegistry {
  connections: CarrierConnection[];
}

/** What the batch is and who owns it; neither comes from the uploaded file. */
export interface ConfirmMarketingImportOptions {
  orderType?: string;
  picUser?: string;
  fileHash?: string;
  filename?: string;
  /** The desk read the duplicate warning and asked for the import anyway. */
  overrideDuplicate?: boolean;
}

/** The batch a re-uploaded file already became, as the server reports it with 409. */
export interface MarketingImportDuplicate {
  batchId: string;
  orderCount: number;
  confirmedAt: string;
}

export interface MarketingImportOrderResult {
  campaignId: string;
  influencerId: string;
  orderId: number;
  created: boolean;
}

/** One confirmed import as the server recorded it: who ran it, when, and what it created. */
export interface MarketingImportBatch {
  batchId: string;
  fileHash: string;
  filename: string | null;
  /** Orders the file named, and of those the ones this run actually created. */
  orderCount: number;
  createdCount: number;
  orderIds: number[];
  orderType: string | null;
  picUser: string | null;
  /** The batch this file had already been imported as, when the desk imported it again anyway. */
  overriddenBatchId: string | null;
  /** The signed-in operator who confirmed it; null only for a batch recorded before operators were. */
  operator: string | null;
  confirmedAt: string;
}

/** Confirmed import batches newest first; `nextCursor` continues from the oldest one returned. */
export interface MarketingImportBatchList {
  items: MarketingImportBatch[];
  nextCursor?: string;
}

export interface SampleRuleSku {
  sku: string;
  qty: number;
}

/**
 * Who a window assigns to. ALL_NEW is decided as orders are created; SELECTED names orders that
 * already exist, so it is applied to them directly.
 */
export type SampleWindowTarget = 'ALL_NEW' | 'SELECTED';

export interface SampleRule {
  ruleKey: string;
  target: SampleWindowTarget;
  orderIds?: number[];
  activeFrom: string;
  /** Null is "forever" — the window has no end, rather than one set far away. */
  activeTo: string | null;
  /**
   * SCHEDULED, ACTIVE, ENDED, or CANCELLED — a window booked for later is not running yet, and one
   * someone stopped is not one that ran out. Only SCHEDULED and ACTIVE can still be cancelled.
   */
  status: string;
  cancelledBy?: string;
  cancelledAt?: string;
}

/** One named order the set could not be given to, in the words the order refused it with. */
export interface SampleSetApplyFailure {
  orderId: number;
  reason: string;
}

/**
 * What opening a window did. A SELECTED window gives the set to its orders on the spot and reports each
 * one; an ALL_NEW window touches no existing order, so it answers with the key alone.
 */
export interface CreatedSampleRule {
  ruleKey: string;
  applied?: number[];
  /** Already carried a sample set, so this window left them alone. */
  skipped?: number[];
  failed?: SampleSetApplyFailure[];
}

/** The window carries a period and a target; the products it gives away are configured once, separately. */
export interface CreateSampleRuleRequest {
  target: SampleWindowTarget;
  orderIds?: number[];
  activeFrom: string;
  activeTo: string | null;
}

/** The one set every open window gives away. */
export interface SampleSet {
  skus: SampleRuleSku[];
}

export interface PicProfile {
  picId: string;
  name: string;
  slackId: string;
  activeDays: Array<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN'>;
  isActive: boolean;
  leaveStart: string | null;
  leaveEnd: string | null;
  weight: number;
  weekendAssignments: string[];
}

export interface PicRoster {
  version: number;
  pics: PicProfile[];
}

/** Adds an order line with a caller-stable key for safe retries. */
export interface AddOrderLineRequest {
  sku: string;
  qtyOrdered: number;
  idempotencyKey: string;
}

/** One part of a line split (SSD-7377); amount goes on every part or on none. */
export interface SplitOrderLinePart {
  /** Units in the line's totalQuantity unit; the parts add up to the line's totalQuantity. */
  quantity: number;
  /** The part's sale total; the parts add up to the line's total to the cent, so a paid order's totals never change. */
  amount?: number;
  /** The waybill this part's parcel arrives under; omitted when it is not known yet. */
  trackingNumber?: string;
  /** The part's display size, 1-64 characters (SSD-7514); omitted keeps the split line's size. */
  size?: string;
  /**
   * Arrived (INBOUNDED or READY) lines only (SSD-7426): true keeps the part's units in stock as arrived; false takes
   * them out of stock and leaves the part awaiting its own inbound scan. Omitted on a line still awaiting its goods.
   */
  arrived?: boolean;
}

/** Splits one line into 2-20 parts; the first stays on the line, the others become new lines in order. */
export interface SplitOrderLineRequest {
  parts: SplitOrderLinePart[];
  idempotencyKey: string;
}

export interface SplitOrderLinePartResult {
  lineNo: number;
  quantity: number;
  lineStatus: string;
  amount: number | null;
  sourcingCost: number | null;
  trackingNumber: string | null;
  /** True when the part's units are on a shelf; false while it waits for them. Absent from a server before SSD-7426. */
  arrived?: boolean;
}

export interface SplitOrderLineResponse {
  orderId: number;
  lineNo: number;
  parts: SplitOrderLinePartResult[];
  fulfillmentStatus: string;
}

/** The longest reason the switch to purchase keeps on the order event and the stock adjustment (SSD-7506). */
export const LINE_TO_JIT_REASON_LIMIT = 500;

/**
 * "No stock → awaiting purchase" (SSD-7506): an existing-inventory line, or `quantity` of its units, turns into a
 * purchase because the shelf does not have them. Omitting `quantity` switches the whole line.
 */
export interface SwitchOrderLineToJitRequest {
  quantity?: number;
  reason: string;
  /** The line's `updatedAt` as the screen read it; a line changed since is refused with CONFLICT. */
  expectedUpdatedAt: string;
  /** One per switch attempt, resent on retry so a switch that landed is answered rather than applied twice. */
  idempotencyKey: string;
}

/** What one shelf held of the sku before and after the switch took the missing units off it. */
export interface SwitchOrderLineToJitStock {
  locationCode: string;
  qty: number;
  onHandBefore: number;
  onHandAfter: number;
}

/**
 * `lineNo` is the line that now awaits purchase: the addressed line when all of it switched, otherwise the part split
 * off it, while `sourceLineNo` keeps `keptQuantity` units from stock.
 */
export interface SwitchOrderLineToJitResponse {
  orderId: number;
  sourceLineNo: number;
  lineNo: number;
  quantity: number;
  keptQuantity: number;
  sourcingRoute: string;
  lineStatus: string;
  fulfillmentStatus: string;
  stock: SwitchOrderLineToJitStock[];
}

/** Whole-line JIT conversion; the authenticated request supplies the actor (SSD-7539). */
export interface SwitchOrderLineToExistingInventoryRequest {
  locationCode: string;
  reason: string;
  expectedUpdatedAt: string;
  idempotencyKey: string;
}

export interface SwitchOrderLineToExistingInventoryResponse {
  orderId: number;
  lineNo: number;
  quantity: number;
  locationCode: string;
  sourcingRoute: string;
  lineStatus: string;
  fulfillmentStatus: string;
  updatedAt: string;
}

export interface ChangeOrderLineQuantityRequest {
  totalQuantity: number;
}

export interface AssignOrderPicRequest {
  picUser: string | null;
}

export interface CorrectOrderTrackingRequest {
  number: string;
  /** Omit to keep the carrier already on the parcel — correcting a number never changes carriers. */
  carrier?: string;
}

export interface ResolveOrderRequest {
  reason: string;
  /** Who is deciding; the API key authenticates the dashboard, not the person. */
  operator?: string;
}

/**
 * Which operation left an order RESOLVED: CLOSE_UNSHIPPED is the Order team's "Closed" (an order closed
 * before it ever shipped), RESOLVE the plain verdict. Wire values; the screens label them.
 */
export type FulfillmentResolutionKind = 'CLOSE_UNSHIPPED' | 'RESOLVE';

/**
 * The statuses the status menu sets on 2.0 through POST /fulfillment/admin/orders/:orderId/status (SSD-7102).
 * CANCELLED is the cancel route, which takes a reason, and OUTBOUNDED is the outbound action.
 */
export type FulfillmentMenuStatus = 'READY' | 'CLOSED' | 'RETURNED';

/** What a status-menu change did; WooCommerce and the customer hear of it from the server, not from the screen. */
export interface FulfillmentOrderStatusChanged {
  fulfillmentStatus: string;
  changedLines: number;
  /** The WooCommerce status the server pushes for it; null when the store is left as it is (RETURNED). */
  wooCommerceStatus: string | null;
}

/** Body of POST /fulfillment/admin/orders/:orderId/closeUnshipped; why the order is being closed. */
export interface CloseUnshippedOrderRequest {
  reason: string;
  /** Who is deciding; the API key authenticates the dashboard, not the person. */
  operator?: string;
}

/** The order reads RESOLVED, with the lines it cancelled and the reservations it gave back. */
export interface CloseUnshippedOrderResponse {
  closed: boolean;
  fulfillmentStatus: string;
  closedLines: number;
  releasedReservations: number;
}

/** Body of POST /fulfillment/admin/orders/:orderId/unresolve; why the verdict is being lifted. */
export interface UnresolveOrderRequest {
  reason: string;
  /** Who is deciding; the API key authenticates the dashboard, not the person. */
  operator?: string;
}

export interface OrderComment {
  text: string;
  author: string;
  at: string;
  /** The product line the note is about (SSD-7265); absent on a note about the order as a whole. */
  lineNo?: number;
}

/** Comment payload with a caller-stable key for safe retries. */
export interface AppendOrderCommentRequest {
  text: string;
  idempotencyKey: string;
  /** Files the note against one product line rather than the order (SSD-7265). */
  lineNo?: number;
}

/**
 * A waybill another order is already travelling under. Not a refusal: the desk sometimes has a real
 * reason to reuse a number, so the save goes through and the screen says whose number it also is.
 */
export interface TrackingInUseWarning {
  code: 'TRACKING_IN_USE';
  orderId: number;
}

/** What a tracking correction settled, plus the warning when the number was not free. */
export interface CorrectOrderTrackingResult {
  changed: boolean;
  warning?: TrackingInUseWarning;
}

export interface ClearOrderTrackingResult {
  trackingId: string;
  deleted: boolean;
}

export interface FulfillmentOrderDetail {
  header: FulfillmentOrderHeader;
  /** How a RESOLVED order was resolved; null on any other status, absent on a server that predates it. */
  resolutionKind?: FulfillmentResolutionKind | null;
  /**
   * Where this order may still go, from the one table the server also refuses moves by. Absent on a
   * server that predates it, which leaves the menu on its older gates rather than blanking it.
   */
  allowedTransitions?: string[];
  lines: FulfillmentOrderLine[];
  reservations: FulfillmentReservation[];
  tracking: FulfillmentTracking[];
  pendingParcels?: FulfillmentPendingParcel[];
  events: FulfillmentEvent[];
  /** Absent on a server that predates clone links; the clone_of segment then still names the source. */
  relations?: FulfillmentOrderRelations;
}

/** One recorded fulfillment write as an audit reader sees it; occurredAt is a raw ISO instant. */
export interface FulfillmentOrderEvent {
  id: string;
  operation: string | null;
  source: string | null;
  operator: string | null;
  occurredAt: string;
  payload: Record<string, unknown> | null;
}

/** The audit trail of one 2.0-native order, newest first. */
export interface FulfillmentOrderEventLog {
  orderId: number;
  events: FulfillmentOrderEvent[];
}

/** The Fulfillment V2 launch switch (SSD-7096): when true, V2 is the only record and the V1 write routes answer 410. */
export interface FulfillmentV2LaunchStateView {
  launched: boolean;
}

/** Admin client for Fulfillment 2.0 order endpoints on internal-api. */
/** One printed row of the internal packing invoice; unknown values arrive as the marker, never blank. */
export interface PackingInvoiceRow {
  no: number;
  eanLast4: string;
  productNameKr: string;
  size: string;
  /** The primary location when the line's stock is split across several. */
  locationCode: string;
  qty: number;
  isSampleSet: boolean;
  /** Every location the row's units come from, primary first; absent on a server that predates it. */
  locations?: FulfillmentLocationQty[];
}

/** One sheet. The server paginates because the break depends on printed measurements it owns. */
export interface PackingInvoicePage {
  pageNo: number;
  pageCount: number;
  orderId: number;
  totalQty: number;
  rows: PackingInvoiceRow[];
  closing: 'TOTAL' | 'CONTINUES';
}

export interface PackingInvoice {
  orderId: number;
  /** When the customer placed the order, as a raw instant; the sheet stamps it on the warehouse clock. */
  orderPlacedAt?: string;
  pages: PackingInvoicePage[];
}

/** What the facade reads to credit an audit row to a person rather than to the shared machine key [D-d]. */
export const OPERATOR_HEADER = 'X-Operator-Id';

/** A hung launch-switch read must settle (as off) so the V1 screens it gates still render (SSD-7096). */
export const LAUNCH_SWITCH_TIMEOUT_MS = 5000;
export const PRE_OUTBOUND_ELIGIBILITY_TIMEOUT_MS = 10000;

export class FulfillmentAdminClient {
  constructor(private readonly apiUrl: string) {}

  /** Built per request, not per client: an operator who signs in mid-session must still be credited. */
  private get headers(): Record<string, string> {
    const operator = OperatorIdentity.current();
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': getConfig().apiKey,
      // Absent rather than blank when nobody is signed in, so the server falls back to the system actor.
      ...(operator ? { [OPERATOR_HEADER]: operator } : {}),
    };
  }

  /**
   * The audit trail of an order WooCommerce never saw. The store owns the history of a commerce order;
   * for a 2.0-native one the fulfillment event log is the only record there is [T08].
   */
  public async listOrderEvents(orderId: number): Promise<FulfillmentOrderEventLog> {
    const response: AxiosResponse<FulfillmentOrderEventLog> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/events`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getLaunchState(): Promise<FulfillmentV2LaunchStateView> {
    const response: AxiosResponse<FulfillmentV2LaunchStateView> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/launchSwitch`,
      { headers: this.headers, timeout: LAUNCH_SWITCH_TIMEOUT_MS },
    );
    return response.data;
  }

  /** The comments hub reads across every order, unlike the order-scoped thread route. */
  public async searchComments(params: SearchCommentsParams): Promise<SearchCommentsResponse> {
    const query = new URLSearchParams();
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.search) {
      query.set('search', params.search);
    }
    if (params.mentions) {
      query.set('mentions', params.mentions);
    }
    if (params.operator) {
      query.set('operator', params.operator);
    }
    if (params.savedOnly) {
      query.set('savedOnly', 'true');
    }
    const qs = query.toString();
    const response: AxiosResponse<SearchCommentsResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/comments${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** One toggle for the hub star: the same route stars and unstars. */
  public async setCommentStar(request: SetCommentStarRequest): Promise<void> {
    await axios.post(`${this.apiUrl}/fulfillment/admin/comments/star`, request, { headers: this.headers });
  }

  public async markCommentsRead(operator: string): Promise<{ lastReadAt: string }> {
    const response: AxiosResponse<{ lastReadAt: string }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/comments/readAll`,
      { operator },
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * The whole filtered set as CSV, which is the point: exporting the page on screen would answer a
   * different question than the filters ask. The server writes an EXPORT event for it [D-s].
   */
  public async exportOrdersCsv(params: ListOrdersParams): Promise<string> {
    const response: AxiosResponse<string> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/export.csv?${this.orderQuery(params).toString()}`,
      { headers: this.headers, responseType: 'text' },
    );
    return response.data;
  }

  public async listOrders(params: ListOrdersParams): Promise<ListOrdersResponse> {
    const qs = this.orderQuery(params).toString();
    const url = `${this.apiUrl}/fulfillment/admin/orders${qs ? `?${qs}` : ''}`;
    const response: AxiosResponse<ListOrdersResponse> = await axios.get(url, { headers: this.headers });
    return response.data;
  }

  public async getOrderDetail(orderId: number): Promise<FulfillmentOrderDetail> {
    const response: AxiosResponse<FulfillmentOrderDetail> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Outbound desks also read the existing label archive when an older tracking row has no label URL. */
  public async getOrderDetailForOutbound(orderId: number): Promise<FulfillmentOrderDetail> {
    const detail = await this.getOrderDetail(orderId);
    return {
      ...detail,
      tracking: await withArchivedOutboundLabels(
        orderId,
        detail.tracking ?? [],
        (id, number) => this.getLabelUrl(id, number),
        detail.pendingParcels,
      ),
    };
  }

  public async getPackingInvoice(orderId: number): Promise<PackingInvoice> {
    const response: AxiosResponse<PackingInvoice> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/packingInvoice`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getPreOutboundCancelEligibility(orderId: number): Promise<PreOutboundCancelEligibility> {
    const response = await axios.get<PreOutboundCancelEligibility>(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/preOutboundCancelEligibility`,
      { headers: this.headers, timeout: PRE_OUTBOUND_ELIGIBILITY_TIMEOUT_MS },
    );
    return response.data;
  }

  /** CS changes record display and audit state; only resolved takes a method, and cancellation affects warehouse work only with explicit consent on an eligible order. */
  public async changeCsStatus(
    orderId: number,
    csStatus: 'delivered' | 'canceled',
    confirmWarehouseCancellation?: boolean,
  ): Promise<ChangeCsStatusResponse>;
  public async changeCsStatus(
    orderId: number,
    csStatus: 'resolved',
    csResolutionMethod: CsResolutionMethod,
  ): Promise<ChangeCsStatusResponse>;
  public async changeCsStatus(
    orderId: number,
    csStatus: ManualCsStatus,
    option?: CsResolutionMethod | boolean,
  ): Promise<ChangeCsStatusResponse> {
    const [token, assertion] = await Promise.all([getAuthorizationToken(), getIdentityAssertion()]);
    const response: AxiosResponse<ChangeCsStatusResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/csStatus`,
      {
        csStatus,
        ...(typeof option === 'string' ? { csResolutionMethod: option } : {}),
        ...(option === true ? { confirmWarehouseCancellation: true } : {}),
      },
      {
        headers: {
          ...this.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(assertion ? { 'x-identity-assertion': assertion } : {}),
        },
      },
    );
    return response.data;
  }

  public async holdOrder(orderId: number, request: HoldOrderRequest): Promise<HoldOrderResponse> {
    const response: AxiosResponse<HoldOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/hold`,
      { reason: request.reason },
      { headers: this.headers },
    );
    return response.data;
  }

  public async releaseHold(orderId: number): Promise<ReleaseHoldResponse> {
    const response: AxiosResponse<ReleaseHoldResponse> = await axios.delete(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/hold`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async updateOrderAddress(
    orderId: number,
    request: UpdateOrderAddressRequest,
  ): Promise<UpdateOrderAddressResponse> {
    const payload: UpdateOrderAddressRequest = {
      ...(request.shippingAddress !== undefined ? { shippingAddress: request.shippingAddress } : {}),
      ...(request.billingAddress !== undefined ? { billingAddress: request.billingAddress } : {}),
      ...(request.consignee !== undefined ? { consignee: request.consignee } : {}),
      ...(request.shippingMessage !== undefined ? { shippingMessage: request.shippingMessage } : {}),
    };
    const response: AxiosResponse<UpdateOrderAddressResponse> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/address`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  public async inboundLine(orderId: number, lineNo: number, request: InboundLineRequest): Promise<LineActionResponse> {
    const response: AxiosResponse<LineActionResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/inbound`,
      { locationCode: request.locationCode },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Takes back one line's arrival scan; 409s when the line has no arrival or was never sourced JIT. */
  public async cancelInbound(
    orderId: number,
    lineNo: number,
    request: CancelInboundLineRequest,
  ): Promise<LineActionResponse> {
    const response: AxiosResponse<LineActionResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/cancelInbound`,
      {
        restock: request.restock,
        ...(request.restockQty !== undefined ? { restockQty: request.restockQty } : {}),
        ...(request.locationCode !== undefined ? { locationCode: request.locationCode } : {}),
        ...(request.memo !== undefined ? { memo: request.memo } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async cancelLine(orderId: number, lineNo: number, request: CancelLineRequest): Promise<LineActionResponse> {
    const response: AxiosResponse<LineActionResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/cancel`,
      {
        reason: request.reason,
        ...(request.receivedGoods === undefined ? {} : { receivedGoods: request.receivedGoods }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async createInboundRequest(request: CreateInboundRequestRequest): Promise<CreateInboundRequestResponse> {
    const payload: CreateInboundRequestRequest = {
      ...(request.idempotencyKey !== undefined ? { idempotencyKey: request.idempotencyKey } : {}),
      source: request.source,
      supplier: request.supplier,
      ...(request.poNo !== undefined ? { poNo: request.poNo } : {}),
      ...(request.otherChannel !== undefined ? { otherChannel: request.otherChannel } : {}),
      ...(request.invoiceNo !== undefined ? { invoiceNo: request.invoiceNo } : {}),
      ...(request.tracking !== undefined ? { tracking: request.tracking } : {}),
      ...(request.expectedAt !== undefined ? { expectedAt: request.expectedAt } : {}),
      ...(request.memo !== undefined ? { memo: request.memo } : {}),
      lines: request.lines,
    };
    const response: AxiosResponse<CreateInboundRequestResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  public async listInboundRequests(params: ListInboundRequestsParams = {}): Promise<ListInboundRequestsResponse> {
    const query = new URLSearchParams();
    if (params.status) {
      query.set('status', params.status);
    }
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.cursor) {
      query.set('cursor', params.cursor);
    }
    const qs = query.toString();
    const response: AxiosResponse<ListInboundRequestsResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/inboundRequests${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getInboundRequest(docId: string): Promise<InboundRequestDetail> {
    const response: AxiosResponse<InboundRequestDetail> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async editInboundRequest(
    docId: string,
    request: EditInboundRequestRequest,
  ): Promise<EditInboundRequestResponse> {
    // The facade credits the edit to the verified staff session; without one it falls back to system.
    const [token, assertion] = await Promise.all([getAuthorizationToken(), getIdentityAssertion()]);
    const response: AxiosResponse<EditInboundRequestResponse> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}`,
      request,
      {
        headers: {
          ...this.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(assertion ? { 'x-identity-assertion': assertion } : {}),
        },
      },
    );
    return response.data;
  }

  public async appendInboundComment(
    docId: string,
    request: AppendInboundCommentRequest,
  ): Promise<{ comments: InboundComment[] }> {
    const operator = OperatorIdentity.current();
    const response: AxiosResponse<{ comments: InboundComment[] }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/comments`,
      { text: request.text, idempotencyKey: request.idempotencyKey, ...(operator && { operator }) },
      { headers: this.headers },
    );
    return response.data;
  }

  public async attachInboundTracking(docId: string, request: AttachInboundTrackingRequest): Promise<void> {
    const payload: AttachInboundTrackingRequest = {
      ...(request.idempotencyKey !== undefined ? { idempotencyKey: request.idempotencyKey } : {}),
      ...(request.tracking !== undefined ? { tracking: request.tracking } : {}),
      ...(request.bulk !== undefined ? { bulk: request.bulk } : {}),
    };
    await axios.patch(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/tracking`,
      payload,
      { headers: this.headers },
    );
  }

  public async countScanInbound(docId: string, request: CountScanInboundRequest): Promise<CountScanInboundResponse> {
    const payload: CountScanInboundRequest = {
      ...(request.barcode !== undefined ? { barcode: request.barcode } : {}),
      ...(request.sku !== undefined ? { sku: request.sku } : {}),
    };
    const response: AxiosResponse<CountScanInboundResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/countScan`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Adds a typed count to a line's running total, so a full box needs no scanning and stations accumulate. */
  public async setInboundLineReceived(
    docId: string,
    lineNo: number,
    request: EditInboundLineReceivedRequest,
  ): Promise<EditInboundLineReceivedResponse> {
    const response: AxiosResponse<EditInboundLineReceivedResponse> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/lines/${lineNo}/received`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Corrects what a line expects when the paperwork was wrong; the confirm gate re-derives from it. */
  public async setInboundLineExpected(
    docId: string,
    lineNo: number,
    request: EditInboundLineQtyRequest,
  ): Promise<EditInboundLineExpectedResponse> {
    const response: AxiosResponse<EditInboundLineExpectedResponse> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/lines/${lineNo}/expected`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  public async assignInboundLocations(docId: string, request: AssignInboundLocationsRequest): Promise<void> {
    await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/locations`,
      {
        assignments: request.assignments.map((assignment) => ({
          sku: assignment.sku,
          locationCode: assignment.locationCode,
        })),
      },
      { headers: this.headers },
    );
  }

  public async cancelInboundRequest(docId: string, request: CancelInboundRequestBody): Promise<ConfirmInboundResponse> {
    const payload: CancelInboundRequestBody = {
      reason: request.reason,
      ...(request.memo !== undefined ? { memo: request.memo } : {}),
    };
    const response: AxiosResponse<ConfirmInboundResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/cancel`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  public async confirmInbound(docId: string, request: ConfirmInboundRequest): Promise<ConfirmInboundResponse> {
    const payload: ConfirmInboundRequest = {
      mode: request.mode,
      ...(request.reason !== undefined ? { reason: request.reason } : {}),
    };
    const response: AxiosResponse<ConfirmInboundResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/inboundRequests/${encodeURIComponent(docId)}/confirm`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  public async listReadyToOutbound(params: ListReadyToOutboundParams = {}): Promise<ListReadyToOutboundResponse> {
    const query = new URLSearchParams();
    if (params.tab != null) {
      query.set('tab', params.tab);
    }
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.beforeOrderId != null) {
      query.set('beforeOrderId', String(params.beforeOrderId));
    }
    if (params.cursorGroup != null) {
      query.set('cursorGroup', params.cursorGroup);
    }
    const qs = query.toString();
    const response: AxiosResponse<ListReadyToOutboundResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/readyToOutbound${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getPickList(orderIds: number[]): Promise<PickListResponse> {
    const query = new URLSearchParams({ orderIds: orderIds.join(',') });
    const response: AxiosResponse<PickListResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/pickList?${query.toString()}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async bulkOutboundOrders(request: BulkOutboundRequest): Promise<BulkOutboundResponse> {
    const checked = await withOutboundCarrierGates(
      request.orderIds.map((orderId) => ({ orderId, violations: [] as string[] })),
      (orderId) => this.getOrderDetailForOutbound(orderId),
    );
    const failed = checked
      .filter((order) => order.violations.length > 0)
      .map((order) => ({
        ...order,
        reason: order.violations.join(' · '),
      }));
    const eligible = checked.filter((order) => order.violations.length === 0).map((order) => order.orderId);
    if (eligible.length === 0) {
      return { applied: [], skipped: [], failed };
    }
    const payload: BulkOutboundRequest = {
      orderIds: eligible,
      ...(request.locationCode !== undefined ? { locationCode: request.locationCode } : {}),
    };
    const response: AxiosResponse<BulkOutboundResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/bulkOutbound`,
      payload,
      { headers: this.headers },
    );
    return { ...response.data, failed: [...response.data.failed, ...failed] };
  }

  public async scanCode(request: ScanCodeRequest): Promise<ScanCodeResponse> {
    const response: AxiosResponse<ScanCodeResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/scan`,
      { code: request.code },
      { headers: this.headers },
    );
    return response.data;
  }

  public async updateTemporaryLocation(orderId: number, request: UpdateTemporaryLocationRequest): Promise<void> {
    await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/temporaryLocation`,
      { value: request.value },
      { headers: this.headers },
    );
  }

  public async bulkInboundLines(orderId: number, request: BulkInboundRequest): Promise<BulkInboundResponse> {
    const payload: BulkInboundRequest = {
      ...(request.lineNos !== undefined ? { lineNos: request.lineNos } : {}),
      ...(request.all !== undefined ? { all: request.all } : {}),
      locationCode: request.locationCode,
    };
    const response: AxiosResponse<BulkInboundResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/bulkInbound`,
      payload,
      { headers: this.headers },
    );
    return response.data;
  }

  /** A short-lived link to the archived carrier label; the bucket is private, so the link is signed per request. */
  public async getLabelUrl(orderId: number, trackingNumber: string): Promise<string> {
    const response: AxiosResponse<{ url: string }> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/trackings/${encodeURIComponent(trackingNumber)}/labelUrl`,
      { headers: this.headers },
    );
    return response.data.url;
  }

  /** The order's packing videos, read live from Zero Packing by internal-api; nothing is stored (SSD-7372). */
  public async listPackingVideos(orderId: string | number): Promise<PackingVideoList> {
    const response: AxiosResponse<PackingVideoList> = await axios.get(
      `${this.apiUrl}/orders/${encodeURIComponent(String(orderId))}/packingVideos`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** A fresh playback link for one of the order's videos; the server refuses a video that is not on that order. */
  public async getPackingVideoPlayback(orderId: string | number, videoId: string): Promise<PackingVideoPlayback> {
    const response: AxiosResponse<PackingVideoPlayback> = await axios.get(
      `${this.apiUrl}/orders/${encodeURIComponent(String(orderId))}/packingVideos/${encodeURIComponent(videoId)}/playback`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Zero Packing hands back the open page when one already exists for the video, so pressing twice is harmless. */
  public async createPackingVideoShareLink(orderId: string | number, videoId: string): Promise<PackingVideoShareLink> {
    const response: AxiosResponse<PackingVideoShareLink> = await axios.post(
      `${this.apiUrl}/orders/${encodeURIComponent(String(orderId))}/packingVideos/${encodeURIComponent(videoId)}/share`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Asks the carrier live for the parcel's trace, by the carrier and carrier order id on its V2 tracking row. Read-only.
   * 404 when the parcel has no carrier order to trace; 502 when the carrier did not answer.
   */
  public async getTrackingTrace(orderId: number, trackingNumber: string): Promise<FulfillmentTrackingTrace> {
    const response: AxiosResponse<FulfillmentTrackingTrace> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/trackings/${encodeURIComponent(trackingNumber)}/trace`,
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Every archived carrier label in the batch as one pdf, plus the orders that contributed none.
   * The bucket has no CORS grant, so the browser cannot merge these itself — the server does it.
   */
  public async getLabelBundle(orderIds: number[]): Promise<LabelBundle> {
    const query = new URLSearchParams({ orderIds: orderIds.join(',') });
    const response: AxiosResponse<ArrayBuffer> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/labelBundle?${query.toString()}`,
      {
        headers: this.headers,
        responseType: 'arraybuffer',
      },
    );
    const skipped = String(response.headers[SKIPPED_ORDERS_HEADER] ?? '')
      .split(',')
      .map((entry) => Number(entry.trim()))
      .filter((entry) => Number.isFinite(entry) && entry > 0);
    return { pdf: new Blob([response.data], { type: 'application/pdf' }), skipped };
  }

  public async getPrintPayload(orderId: number): Promise<Record<string, unknown>> {
    const response: AxiosResponse<Record<string, unknown>> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/printPayload`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async startClosing(request: StartClosingRequest): Promise<StartClosingResponse> {
    const response: AxiosResponse<StartClosingResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing`,
      { expectedCount: request.expectedCount },
      { headers: this.headers },
    );
    return response.data;
  }

  public async listClosingSessions(limit?: number): Promise<ClosingSessionListResponse> {
    const query = new URLSearchParams();
    if (limit != null) {
      query.set('limit', String(limit));
    }
    const qs = query.toString();
    const response: AxiosResponse<ClosingSessionListResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/closing${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getClosingSession(docId: string): Promise<ClosingSessionDetail> {
    const response: AxiosResponse<ClosingSessionDetail> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async scanClosing(docId: string, request: ScanClosingRequest): Promise<ScanClosingResponse> {
    const response: AxiosResponse<ScanClosingResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/scan`,
      { waybill: request.waybill },
      { headers: this.headers },
    );
    return response.data;
  }

  public async deleteClosingScan(docId: string, lineNo: number): Promise<DeleteClosingScanResponse> {
    const response: AxiosResponse<DeleteClosingScanResponse> = await axios.delete(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/scans/${lineNo}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async exportClosingCsv(docId: string): Promise<string> {
    const response: AxiosResponse<string> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/exportCsv`,
      { headers: this.headers, responseType: 'text' },
    );
    return response.data;
  }

  public async editClosingTarget(docId: string, request: EditClosingTargetRequest): Promise<EditClosingTargetResponse> {
    const response: AxiosResponse<EditClosingTargetResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/editTarget`,
      {
        expectedCount: request.expectedCount,
        ...(request.previousExpectedCount !== undefined && { previousExpectedCount: request.previousExpectedCount }),
        ...(request.expectedVersion !== undefined && { expectedVersion: request.expectedVersion }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async startClosingAmendment(docId: string): Promise<ClosingAmendmentResponse> {
    const response: AxiosResponse<ClosingAmendmentResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/amend`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async exitClosingAmendment(docId: string): Promise<ClosingAmendmentResponse> {
    const response: AxiosResponse<ClosingAmendmentResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/exitAmend`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async processClosingScanOrder(
    docId: string,
    lineNo: number,
    request: ProcessClosingScanOrderRequest,
  ): Promise<ProcessClosingScanOrderResponse> {
    const response: AxiosResponse<ProcessClosingScanOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/scans/${lineNo}/processOutbound`,
      {
        zeroPackingVerified: request.zeroPackingVerified,
        idempotencyKey: request.idempotencyKey,
        ...(request.memo !== undefined && { memo: request.memo }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async cancelClosing(docId: string): Promise<CancelClosingResponse> {
    const response: AxiosResponse<CancelClosingResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/cancel`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async confirmClosing(docId: string, request: ConfirmClosingRequest): Promise<ConfirmClosingResponse> {
    const response: AxiosResponse<ConfirmClosingResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/closing/${encodeURIComponent(docId)}/confirm`,
      request.force !== undefined ? { force: request.force } : {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async listInventory(params: ListInventoryParams = {}, signal?: AbortSignal): Promise<InventoryPage> {
    const query = new URLSearchParams();
    if (params.skus?.length) {
      query.set('sku', params.skus.join(','));
    }
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.includeEmpty) {
      query.set('includeEmpty', 'true');
    }
    if (params.cursor) {
      query.set('cursor', params.cursor);
    }
    const qs = query.toString();
    const response: AxiosResponse<InventoryPage> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/inventory${qs ? `?${qs}` : ''}`,
      { headers: this.headers, ...(signal ? { signal } : {}) },
    );
    return response.data;
  }

  /**
   * A sku reserved on two locations has two holder sets, so a shelf narrows the list to its own. Without
   * one the server answers every active holder of the sku, including those pinned to no shelf.
   */
  public async listReservationHolders(sku: string, locationCode?: string): Promise<{ holders: ReservationHolder[] }> {
    const query = locationCode !== undefined ? `?locationCode=${encodeURIComponent(locationCode)}` : '';
    const response: AxiosResponse<{ holders: ReservationHolder[] }> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/inventory/${encodeURIComponent(sku)}/reservations${query}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async getStockLedger(params: GetStockLedgerParams): Promise<StockLedgerResponse> {
    const query = new URLSearchParams(
      'sku' in params ? { sku: params.sku } : { searchField: params.searchField, keyword: params.keyword },
    );
    if (params.movementType) {
      query.set('movementType', params.movementType);
    }
    if (params.origin) {
      query.set('origin', params.origin);
    }
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.cursor) {
      query.set('cursor', params.cursor);
    }
    const response: AxiosResponse<StockLedgerResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/stockLedger?${query.toString()}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async releaseReservation(request: ReleaseReservationRequest): Promise<{ released: boolean }> {
    const response: AxiosResponse<{ released: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/reservations/release`,
      {
        orderId: request.orderId,
        lineNo: request.lineNo,
        reason: request.reason,
        restock: request.restock,
        ...(request.restockQty !== undefined ? { restockQty: request.restockQty } : {}),
        ...(request.locationCode !== undefined ? { locationCode: request.locationCode } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async moveStock(request: MoveStockRequest): Promise<{ moved: boolean }> {
    const response: AxiosResponse<{ moved: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stock/move`,
      {
        sku: request.sku,
        fromLocation: request.fromLocation,
        toLocation: request.toLocation,
        qty: request.qty,
        idempotencyKey: request.idempotencyKey,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async returnPutaway(orderId: number, request: ReturnPutawayRequest): Promise<{ putAway: boolean }> {
    const response: AxiosResponse<{ putAway: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/returnPutaway`,
      {
        ...(request.idempotencyKey !== undefined && { idempotencyKey: request.idempotencyKey }),
        lineNo: request.lineNo,
        locationCode: request.locationCode,
        ...(request.qty !== undefined && { qty: request.qty }),
        ...(request.memo !== undefined && { memo: request.memo }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async openStockTake(request: OpenStockTakeRequest): Promise<{ docId: string }> {
    const response: AxiosResponse<{ docId: string }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stockTakes`,
      {
        locationCode: request.locationCode,
        idempotencyKey: request.idempotencyKey,
        ...(request.auditGroupId !== undefined ? { auditGroupId: request.auditGroupId } : {}),
        ...(request.targetSkus !== undefined ? { targetSkus: request.targetSkus } : {}),
        ...(request.origin !== undefined ? { origin: request.origin } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async listStockTakes(params: ListStockTakesParams = {}): Promise<StockTakeListResponse> {
    const query = new URLSearchParams();
    if (params.status) {
      query.set('status', params.status);
    }
    if (params.locationCode) {
      query.set('locationCode', params.locationCode);
    }
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.origin) {
      query.set('origin', params.origin);
    }
    if (params.before) {
      query.set('before', params.before);
    }
    const qs = query.toString();
    const response: AxiosResponse<StockTakeListResponse> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/stockTakes${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async countStockTake(docId: string, request: CountStockTakeRequest): Promise<Record<string, unknown>> {
    const response: AxiosResponse<Record<string, unknown>> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stockTakes/${encodeURIComponent(docId)}/count`,
      {
        sku: request.sku,
        countedQty: request.countedQty,
        // Only a non-blank note travels: the server refuses an empty one, and no note is not an empty note.
        ...(request.reason?.trim() ? { reason: request.reason.trim() } : {}),
        ...(request.expectedSystemQty !== undefined && { expectedSystemQty: request.expectedSystemQty }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async getStockTake(docId: string): Promise<StockTakeDetail> {
    const response: AxiosResponse<StockTakeDetail> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/stockTakes/${encodeURIComponent(docId)}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Closes a round without applying a thing — the way out of a round that should never have been opened.
   * The principal is the server's to record: sending one is a 400, so the body carries the reason and nothing else.
   */
  public async cancelStockTake(docId: string, reason?: string): Promise<StockTakeCancelled> {
    const response: AxiosResponse<StockTakeCancelled> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stockTakes/${encodeURIComponent(docId)}/cancel`,
      reason ? { reason } : {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async confirmStockTake(docId: string): Promise<Record<string, unknown>> {
    const response: AxiosResponse<Record<string, unknown>> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stockTakes/${encodeURIComponent(docId)}/confirm`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async listUnrecognizedScans(params: ListUnrecognizedScansParams = {}): Promise<UnrecognizedScanPool> {
    const query = new URLSearchParams();
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.cursor) {
      query.set('cursor', params.cursor);
    }
    const qs = query.toString();
    const response: AxiosResponse<UnrecognizedScanPool> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Reads one page of an entry's candidates; 404s on an unknown entry and 409s on one no longer open. */
  public async listUnrecognizedScanCandidates(
    id: string,
    params: ListUnrecognizedScansParams = {},
  ): Promise<UnrecognizedScanCandidatePage> {
    const query = new URLSearchParams();
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    if (params.cursor) {
      query.set('cursor', params.cursor);
    }
    const qs = query.toString();
    const response: AxiosResponse<UnrecognizedScanCandidatePage> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans/${encodeURIComponent(id)}/suspectedOrders${qs ? `?${qs}` : ''}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Finds the order lines a purchase order number placed, for the desk's M2 lookup; an unmatched number answers with no candidates. */
  public async lookupPurchaseOrder(orderNo: string): Promise<PurchaseOrderLookup> {
    const response: AxiosResponse<PurchaseOrderLookup> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/purchaseOrderLookup?orderNo=${encodeURIComponent(orderNo)}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Parks one parcel the desk could not place, and hands back the id the pool screen works it by. */
  public async registerUnrecognizedScan(request: RegisterUnrecognizedScanRequest): Promise<UnrecognizedScanRegistered> {
    const response: AxiosResponse<UnrecognizedScanRegistered> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans`,
      {
        code: request.code,
        // The products travel as a list when the desk named them; a parcel with none named goes as one count.
        ...(request.quantity !== undefined && { quantity: request.quantity }),
        ...(request.items !== undefined && { items: request.items }),
        ...(request.codeKind !== undefined && { codeKind: request.codeKind }),
        // Each detail travels only when the desk gave one: an empty string would read as a declared value.
        ...(request.scanId !== undefined && { scanId: request.scanId }),
        ...(request.trackingNo !== undefined && { trackingNo: request.trackingNo }),
        ...(request.orderNo !== undefined && { orderNo: request.orderNo }),
        ...(request.sku !== undefined && { sku: request.sku }),
        ...(request.memo !== undefined && { memo: request.memo }),
        ...(request.temporaryLocation !== undefined && { temporaryLocation: request.temporaryLocation }),
        // Only ever sent as true: the server's default is to refuse a code it cannot place.
        ...(request.force !== undefined && { force: request.force }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async resolveUnrecognizedScan(
    id: string,
    request: ResolveUnrecognizedScanRequest,
  ): Promise<{ resolved: boolean }> {
    const response: AxiosResponse<{ resolved: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans/${encodeURIComponent(id)}/resolve`,
      {
        sku: request.sku,
        // Sent as the pair the type guarantees, so the body can never carry half a line reference.
        ...(request.orderId !== undefined && request.lineNo !== undefined
          ? { orderId: request.orderId, lineNo: request.lineNo }
          : {}),
        ...(request.via !== undefined && { via: request.via }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Parks the entry out of the pool with a reason; 409s when it is no longer open. */
  public async removeUnrecognizedScan(
    id: string,
    request: RemoveUnrecognizedScanRequest,
  ): Promise<{ removed: boolean }> {
    const response: AxiosResponse<{ removed: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans/${encodeURIComponent(id)}/remove`,
      {
        reason: request.reason,
        ...(request.inboundNo !== undefined ? { inboundNo: request.inboundNo } : {}),
        ...(request.memo !== undefined ? { memo: request.memo } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Takes every open row of one parcel out of the pool under one reason, in one write; 409s when nothing of it is open. */
  public async removeUnrecognizedParcel(
    parcelId: string,
    request: RemoveUnrecognizedScanRequest,
  ): Promise<{ removed: boolean; removedCount?: number }> {
    const response: AxiosResponse<{ removed: boolean; removedCount?: number }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/unrecognizedScans/parcels/${encodeURIComponent(parcelId)}/remove`,
      {
        reason: request.reason,
        ...(request.inboundNo !== undefined ? { inboundNo: request.inboundNo } : {}),
        ...(request.memo !== undefined ? { memo: request.memo } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async getMarketingImportTemplate(): Promise<MarketingImportTemplate> {
    const response: AxiosResponse<MarketingImportTemplate> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/marketingImports/template`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async previewMarketingImport(csv: string): Promise<MarketingImportPreview> {
    const response: AxiosResponse<MarketingImportPreview> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/marketingImports/preview`,
      { csv },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Raises the previewed orders. Rejects with 400 when any destination has no connected carrier. */
  public async confirmMarketingImport(
    rows: MarketingImportRow[],
    options: ConfirmMarketingImportOptions = {},
  ): Promise<{ orders: MarketingImportOrderResult[] }> {
    const response: AxiosResponse<{ orders: MarketingImportOrderResult[] }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/marketingImports`,
      {
        // A previewed row also carries the product name and carrier the preview looked up, and the
        // confirm rejects the whole file for them — only the file's own columns are sent back.
        // The address details a label needs ride along when the file had them (SSD-7448): without them the server's
        // own row check refuses a US, CA or AU row the preview had accepted.
        rows: rows.map((row) => ({
          campaignId: row.campaignId,
          influencerId: row.influencerId,
          ...(row.owner ? { owner: row.owner } : {}),
          sku: row.sku,
          qty: row.qty,
          recipientName: row.recipientName,
          recipientPhone: row.recipientPhone,
          ...(row.recipientEmail ? { recipientEmail: row.recipientEmail } : {}),
          addressLine1: row.addressLine1,
          ...(row.addressLine2 ? { addressLine2: row.addressLine2 } : {}),
          city: row.city,
          ...(row.state ? { state: row.state } : {}),
          countryCode: row.countryCode,
          ...(row.postcode ? { postcode: row.postcode } : {}),
        })),
        ...(options.orderType ? { orderType: options.orderType } : {}),
        ...(options.picUser ? { picUser: options.picUser } : {}),
        ...(options.fileHash ? { fileHash: options.fileHash } : {}),
        ...(options.filename ? { filename: options.filename } : {}),
        ...(options.overrideDuplicate ? { overrideDuplicate: true } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async listMarketingImportBatches(limit: number): Promise<MarketingImportBatchList> {
    const response: AxiosResponse<MarketingImportBatchList> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/marketingImports`,
      { headers: this.headers, params: { limit } },
    );
    return response.data;
  }

  public async getCarrierRegistry(): Promise<CarrierRegistry> {
    const response: AxiosResponse<CarrierRegistry> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/carrierRegistry`,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }

  /** Replaces the registry wholesale; a country left out of the list is disconnected. */
  public async updateCarrierRegistry(connections: CarrierConnection[]): Promise<void> {
    await axios.put(`${this.apiUrl}/fulfillment/admin/carrierRegistry`, { connections }, { headers: this.headers });
  }

  public async listSampleRules(): Promise<{ rules: SampleRule[] }> {
    const response: AxiosResponse<{ rules: SampleRule[] }> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/sampleRules`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async createSampleRule(request: CreateSampleRuleRequest): Promise<CreatedSampleRule> {
    const response: AxiosResponse<CreatedSampleRule> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/sampleRules`,
      {
        target: request.target,
        ...(request.orderIds ? { orderIds: request.orderIds } : {}),
        activeFrom: request.activeFrom,
        activeTo: request.activeTo,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async getSampleSet(): Promise<SampleSet> {
    const response: AxiosResponse<SampleSet> = await axios.get(`${this.apiUrl}/fulfillment/admin/sampleSet`, {
      headers: this.headers,
    });
    return response.data;
  }

  /** Replaces the set wholesale; an empty list stops assignment without cancelling any window. */
  public async updateSampleSet(skus: SampleRuleSku[]): Promise<void> {
    await axios.put(`${this.apiUrl}/fulfillment/admin/sampleSet`, { skus }, { headers: this.headers });
  }

  public async cancelSampleRule(ruleKey: string): Promise<{ cancelled: boolean }> {
    const response: AxiosResponse<{ cancelled: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/sampleRules/${encodeURIComponent(ruleKey)}/cancel`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async getPicRoster(): Promise<PicRoster> {
    const response: AxiosResponse<PicRoster> = await axios.get(`${this.apiUrl}/fulfillment/admin/picRoster`, {
      headers: this.headers,
    });
    return response.data;
  }

  public async savePicRoster(pics: PicProfile[]): Promise<PicRoster> {
    const response: AxiosResponse<PicRoster> = await axios.put(
      `${this.apiUrl}/fulfillment/admin/picRoster`,
      { pics },
      { headers: this.headers },
    );
    return response.data;
  }

  public async addOrderLine(orderId: number, request: AddOrderLineRequest): Promise<{ lineNo: number }> {
    const response: AxiosResponse<{ lineNo: number }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines`,
      { sku: request.sku, qtyOrdered: request.qtyOrdered, idempotencyKey: request.idempotencyKey },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Splits one line into parts that arrive separately, each with its own quantity, amount and waybill. */
  public async splitOrderLine(
    orderId: number,
    lineNo: number,
    request: SplitOrderLineRequest,
  ): Promise<SplitOrderLineResponse> {
    const response: AxiosResponse<SplitOrderLineResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/split`,
      {
        parts: request.parts.map((part) => ({
          quantity: part.quantity,
          ...(part.amount !== undefined ? { amount: part.amount } : {}),
          trackingNumber: part.trackingNumber,
          ...(part.size !== undefined ? { size: part.size } : {}),
          ...(part.arrived !== undefined ? { arrived: part.arrived } : {}),
        })),
        idempotencyKey: request.idempotencyKey,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Switches an existing-inventory line, or some of its units, to purchase because the shelf has none (SSD-7506). */
  public async switchOrderLineToJit(
    orderId: number,
    lineNo: number,
    request: SwitchOrderLineToJitRequest,
  ): Promise<SwitchOrderLineToJitResponse> {
    const response: AxiosResponse<SwitchOrderLineToJitResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/switchToJit`,
      {
        ...(request.quantity !== undefined ? { quantity: request.quantity } : {}),
        reason: request.reason,
        expectedUpdatedAt: request.expectedUpdatedAt,
        idempotencyKey: request.idempotencyKey,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async switchOrderLineToExistingInventory(
    orderId: number,
    lineNo: number,
    request: SwitchOrderLineToExistingInventoryRequest,
  ): Promise<SwitchOrderLineToExistingInventoryResponse> {
    const response: AxiosResponse<SwitchOrderLineToExistingInventoryResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/switchToExistingInventory`,
      {
        locationCode: request.locationCode,
        reason: request.reason,
        expectedUpdatedAt: request.expectedUpdatedAt,
        idempotencyKey: request.idempotencyKey,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  public async changeOrderLineQuantity(
    orderId: number,
    lineNo: number,
    request: ChangeOrderLineQuantityRequest,
  ): Promise<{ changed: boolean }> {
    const response: AxiosResponse<{ changed: boolean }> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}`,
      { totalQuantity: request.totalQuantity },
      { headers: this.headers },
    );
    return response.data;
  }

  public async assignOrderPic(orderId: number, request: AssignOrderPicRequest): Promise<{ changed: boolean }> {
    const response: AxiosResponse<{ changed: boolean }> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/pic`,
      { picUser: request.picUser },
      { headers: this.headers },
    );
    return response.data;
  }

  public async correctOrderTracking(
    orderId: number,
    request: CorrectOrderTrackingRequest,
  ): Promise<CorrectOrderTrackingResult> {
    const response: AxiosResponse<CorrectOrderTrackingResult> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/tracking`,
      { number: request.number, carrier: request.carrier },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Takes the waybill off the order entirely, which correcting it to an empty string cannot do. */
  public async clearOrderTracking(orderId: number): Promise<ClearOrderTrackingResult> {
    const response: AxiosResponse<ClearOrderTrackingResult> = await axios.delete(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/tracking`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async resolveOrder(orderId: number, request: ResolveOrderRequest): Promise<{ resolved: boolean }> {
    const response: AxiosResponse<{ resolved: boolean }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/resolve`,
      { reason: request.reason, operator: request.operator },
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Sets a status from the status menu once Fulfillment 2.0 has launched (SSD-7187), in place of V1's
   * `PUT /orders/:orderId/status`, which answers 410 then. READY also sends the Preparing Order email, once per order.
   */
  public async changeOrderStatus(
    orderId: number,
    status: FulfillmentMenuStatus,
    operator: string,
  ): Promise<FulfillmentOrderStatusChanged> {
    const response: AxiosResponse<FulfillmentOrderStatusChanged> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/status`,
      { status, operator },
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Closes an order that never shipped: its open lines are cancelled and their held stock released.
   * V2 records only — WooCommerce is not changed and the customer gets no email.
   */
  public async closeUnshippedOrder(
    orderId: number,
    request: CloseUnshippedOrderRequest,
  ): Promise<CloseUnshippedOrderResponse> {
    const response: AxiosResponse<CloseUnshippedOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/closeUnshipped`,
      { reason: request.reason, operator: request.operator },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Lifts a manual RESOLVED verdict; the reply carries the status the order's lines derive to. */
  public async unresolveOrder(
    orderId: number,
    request: UnresolveOrderRequest,
  ): Promise<{ unresolved: boolean; fulfillmentStatus: string }> {
    const response: AxiosResponse<{ unresolved: boolean; fulfillmentStatus: string }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/unresolve`,
      { reason: request.reason, operator: request.operator },
      { headers: this.headers },
    );
    return response.data;
  }

  public async listOrderComments(orderId: number): Promise<{ comments: OrderComment[] }> {
    const response: AxiosResponse<{ comments: OrderComment[] }> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/comments`,
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Books the carrier for an order that only exists in Fulfillment 2.0. The OMS reset route reads the
   * V1 store and answers 404 for these, so this is the only way such an order reaches a carrier.
   */
  public async createCarrierOrder(orderId: number, request: CreateCarrierOrderRequest): Promise<CarrierOrderResponse> {
    const response: AxiosResponse<CarrierOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/carrierOrder`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Clones the order into a new internal-band order and books its carrier in the same call. The carrier
   * step can fail on its own; the clone then stands and the response says why the booking did not.
   */
  public async cloneOrder(orderId: number, request: CloneOrderRequest): Promise<CloneOrderResponse> {
    const response: AxiosResponse<CloneOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/clone`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Reissues the carrier order from the 2.0-stored address, so an address edited here reaches the new label. */
  public async resetCarrierOrder(
    orderId: number,
    request: ResetCarrierOrderRequest,
  ): Promise<ResetCarrierOrderResponse> {
    const response: AxiosResponse<ResetCarrierOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/carrierOrder/reset`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * The carrier order the order holds now, as the list's outbound parcel reads it. The detail view does
   * not carry the carrier order id, and the switch must name the one the operator was shown.
   */
  public async getOrderShipment(orderId: number): Promise<FulfillmentOrderShipmentSummary | null> {
    // Newest first below orderId + 1, so the order itself is the first row whenever it exists.
    const page = await this.listOrders({
      search: String(orderId),
      searchOrderNo: true,
      beforeOrderId: orderId + 1,
      limit: 1,
    });
    const row = page.orders.find((order) => order.orderId === orderId);
    return row?.shipment ?? null;
  }

  /** Cancels the order's carrier order and books the chosen carrier, in Fulfillment 2.0 only (SSD-7064). */
  public async switchCarrierOrder(
    orderId: number,
    request: SwitchCarrierOrderRequest,
  ): Promise<SwitchCarrierOrderResponse> {
    // The facade credits the switch to the verified staff session; without one it falls back to system.
    const [token, assertion] = await Promise.all([getAuthorizationToken(), getIdentityAssertion()]);
    const response: AxiosResponse<SwitchCarrierOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/carrierOrder/switch`,
      request,
      {
        headers: {
          ...this.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(assertion ? { 'x-identity-assertion': assertion } : {}),
        },
      },
    );
    return response.data;
  }

  /** Records a manual outbound or correction on the V2 stock ledger; refused with 409 until launch (SSD-7099). */
  public async adjustStock(request: AdjustStockRequest): Promise<AdjustStockResponse> {
    // The facade credits the change to the verified staff session; without one it falls back to system.
    const [token, assertion] = await Promise.all([getAuthorizationToken(), getIdentityAssertion()]);
    const response: AxiosResponse<AdjustStockResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/stock/adjust`,
      request,
      {
        headers: {
          ...this.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(assertion ? { 'x-identity-assertion': assertion } : {}),
        },
      },
    );
    return response.data;
  }

  /**
   * Every screen that writes a note goes through here, so the thread names the person on all of them.
   * A memo about one line travels with its line number (SSD-7265); an order note sends none.
   */
  public async appendOrderComment(
    orderId: number,
    request: AppendOrderCommentRequest,
  ): Promise<{ comments: OrderComment[] }> {
    const operator = OperatorIdentity.current();
    const response: AxiosResponse<{ comments: OrderComment[] }> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/comments`,
      {
        text: request.text,
        idempotencyKey: request.idempotencyKey,
        ...(request.lineNo !== undefined && { lineNo: request.lineNo }),
        ...(operator && { operator }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Takes one note off the thread by the time it was stored; the answer is the thread as it now reads (SSD-7265). */
  public async removeOrderComment(orderId: number, at: string): Promise<{ comments: OrderComment[] }> {
    const response: AxiosResponse<{ comments: OrderComment[] }> = await axios.delete(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/comments/${encodeURIComponent(at)}`,
      { headers: this.headers },
    );
    return response.data;
  }

  public async outboundOrder(orderId: number): Promise<OutboundOrderResponse> {
    const block = await readOutboundCarrierBlock(orderId, (id) => this.getOrderDetailForOutbound(id));
    if (block) {
      throw new OutboundCarrierGateError(block);
    }
    const response: AxiosResponse<OutboundOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/outbound`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * Cancels a whole fulfillment order and releases the reservations its lines held. Marketing and
   * internal orders never reach WooCommerce, so this is the only cancel that can reach them; once
   * Fulfillment 2.0 has launched it cancels a web order end to end too. The cancel type (SSD-7450) is
   * left out when absent, so an older server reads the request as it always did.
   */
  public async cancelFulfillmentOrder(
    orderId: number,
    reason: string,
    receivedGoods?: ReceivedGoods,
    cancelType?: OrderCancelTypeRequest,
  ): Promise<CancelFulfillmentOrderResponse> {
    const response: AxiosResponse<CancelFulfillmentOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/cancel`,
      {
        reason,
        ...(receivedGoods === undefined ? {} : { receivedGoods }),
        ...(cancelType === undefined ? {} : { cancelType: cancelType.cancelType }),
        ...(cancelType?.customerRequestedAt === undefined
          ? {}
          : { customerRequestedAt: cancelType.customerRequestedAt }),
        ...(cancelType?.lockOverrideReason === undefined ? {} : { lockOverrideReason: cancelType.lockOverrideReason }),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** Patches the agent-filled purchase provenance of one line; only the sent fields change. */
  /** Folds duplicate orders into the target; the server enforces BR-18 and cancels the sources. */
  public async mergeOrders(request: MergeOrdersRequest): Promise<MergeOrdersResponse> {
    const response: AxiosResponse<MergeOrdersResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/merge`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** The message the COGS report would post for a line, without posting it; the purchase price is left out while empty. */
  public async previewLineCogsReport(
    orderId: number,
    lineNo: number,
    purchasePrice: number | null,
  ): Promise<CogsReportPreview> {
    const query =
      purchasePrice === null ? '' : `?${new URLSearchParams({ purchasePrice: String(purchasePrice) }).toString()}`;
    const response: AxiosResponse<CogsReportPreview> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/cogsReport/preview${query}`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Posts one line's COGS report to the catalog team's Slack channel; it answers only once the post happened. */
  public async reportLineCogs(
    orderId: number,
    lineNo: number,
    request: ReportLineCogsRequest,
  ): Promise<ReportLineCogsResponse> {
    const response: AxiosResponse<ReportLineCogsResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/cogsReport`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** The catalog request types and the fields each takes (SSD-7384). */
  public async getCatalogRequestSchema(): Promise<CatalogRequestSchema> {
    const response: AxiosResponse<CatalogRequestSchema> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/catalogRequest/schema`,
      { headers: this.headers },
    );
    return response.data;
  }

  /** The message a catalog request would post for a line, without posting it. */
  public async previewCatalogRequest(
    orderId: number,
    lineNo: number,
    request: PreviewCatalogRequestRequest,
  ): Promise<CatalogRequestPreview> {
    const response: AxiosResponse<CatalogRequestPreview> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/catalogRequest/preview`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Posts one line's catalog request to the catalog team's Slack channel; it answers only once the post happened. */
  public async sendCatalogRequest(
    orderId: number,
    lineNo: number,
    request: SendCatalogRequestRequest,
  ): Promise<SendCatalogRequestResponse> {
    const response: AxiosResponse<SendCatalogRequestResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/catalogRequest`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  public async updateLineAgentTracking(
    orderId: number,
    lineNo: number,
    request: UpdateLineAgentTrackingRequest,
  ): Promise<LineAgentTrackingResponse> {
    const response: AxiosResponse<LineAgentTrackingResponse> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/agentTracking`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  /** Records the seller type and purchase-time price comparison of one line (SSD-7177). */
  public async updateLineSourcingDetail(
    orderId: number,
    lineNo: number,
    request: UpdateLineSourcingDetailRequest,
  ): Promise<{ changed: boolean }> {
    const response: AxiosResponse<{ changed: boolean }> = await axios.patch(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/lines/${lineNo}/sourcingDetail`,
      request,
      { headers: this.headers },
    );
    return response.data;
  }

  public async cancelOutbound(orderId: number): Promise<CancelOutboundOrderResponse> {
    const response: AxiosResponse<CancelOutboundOrderResponse> = await axios.post(
      `${this.apiUrl}/fulfillment/admin/orders/${orderId}/cancelOutbound`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  /** One place builds the filter query, so the export can never disagree with the list it exports. */
  private orderQuery(params: ListOrdersParams): URLSearchParams {
    const query = new URLSearchParams();
    // A blank filter is not a filter, so only non-empty values reach the query.
    const filters: Array<[string, string | undefined]> = [
      ['band', params.band],
      ['channel', params.channel],
      ['fulfillmentStatus', params.fulfillmentStatus],
      ['csStatus', params.csStatus],
      ['csResolutionMethod', params.csResolutionMethod],
      ['startDate', params.startDate],
      ['endDate', params.endDate],
      ['countryCode', params.countryCode],
      ['pic', params.pic],
      ['search', params.search],
      ['purchaseOrder', params.purchaseOrder],
      ['inboundTracking', params.inboundTracking],
    ];
    for (const [key, value] of filters) {
      if (value) {
        query.set(key, value);
      }
    }
    // Numbers carry a meaningful zero-ish case, so they are checked for absence rather than truthiness.
    if (params.limit != null) {
      query.set('limit', String(params.limit));
    }
    // Scope flags only mean something alongside a term, and false is meaningful — so send them explicitly.
    if (params.search) {
      if (params.searchOrderNo != null) {
        query.set('searchOrderNo', String(params.searchOrderNo));
      }
      if (params.searchTracking != null) {
        query.set('searchTracking', String(params.searchTracking));
      }
      if (params.searchComments != null) {
        query.set('searchComments', String(params.searchComments));
      }
      if (params.searchPic != null) {
        query.set('searchPic', String(params.searchPic));
      }
    }
    if (params.offset) {
      query.set('offset', String(params.offset));
    }
    if (params.beforeOrderId != null) {
      query.set('beforeOrderId', String(params.beforeOrderId));
    }
    if (params.onHoldOnly) {
      query.set('onHoldOnly', 'true');
    }
    return query;
  }
}
